import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useSaveFlow, useDeleteFlow, showDialog } from '../../components/confirm';
import { AuditTrailButton } from '../../components/AuditTrailButton';
import { AppToolbar } from '../../components/AppToolbar/AppToolbar';
import { ExportMenu } from '../../components/AppToolbar/ExportMenu';
import { useHotkeys, formatCombo, type HotkeyDef } from '../../components/hotkeys';
import { buildSourceOptions, reconcileSources } from './dupSpikeSource';
import { InlineCellEditor } from '../../components/gridEdit/InlineCellEditor';
import { fmtLocalDate, fmtLocalShort, toWire } from '../../utils/datetime';
// Shared column model + header menu (client 2026-08-05): the NON-frozen data
// columns of the prep-sequence grid get column RESIZE + right-click Hide / Show
// All / Column Chooser (no column reorder; the 7 frozen columns stay pinned). Sort +
// filter are VIEW-ONLY via useSeqGridView (2026-09-28) — the run order is untouched.
import { useColumnManager, useHeaderMenu } from '../../components/AppDataGrid';
import { useSeqGridView, frozenWidthVars, SEQ_VIEW_LOCK_MSG } from './useSeqGridView';
import { SeqViewNote } from './SeqViewNote';
import {
  prepBatchApi,
  type QcJob, type QcInstrument, type QcTypeOption,
  type QcLinkedReagent, type QcAvailableReagent,
  type PrepSample, type PrepSeqRow, type PrepHeader, type PrepPreviousBatch,
  type PrepSaveResult,
} from './prepBatch.api';
// COA preview is reagent-scoped (not batch-scoped) — the prep contract has no COA
// endpoint of its own, so the Others-tab COA link reuses the QC reagent COA reader
// (same underlying dbo.Reagent). Read-only reuse; the QC module is not modified.
import { qcBatchApi } from './qcBatch.api';
import { useAuthStore } from '../../auth/store';
import { useUnsavedGuard, confirmDiscardIfUnsaved } from '../../hooks/useUnsavedGuard';
import { WorkflowStepperBar, type StepState } from './WorkflowStepperBar';
import { useMiniGrid, popupWidthFor, type MiniCol } from './useMiniGrid';
import { OtherSection, useOthersReagentGrid, useOthersInstrumentGrid } from './othersGrids';
import './qcBatch.css';

import { samplePrepFromNtu } from './prepNtu';

// ════════════════════════════════════════════════════════════════════════════
// Sample Prep Batch detail screen — the TWIN of QcBatchDetailPage, opened from a
// Sample Prep Queue row (SCREEN 2 of Sample-Prep-Batch-Prototype.html).  The
// test method is ALWAYS known (seeded from the queue row) so there is NO
// Matrix→Test→Method pick cascade, and NO Template / Prep-Batch pickers.  Flow:
// pick Job(s) + Instrument → Load (pending samples) → Sort (server QC
// interleave) → OK (persist).  All shared components (useNotify / useSaveFlow /
// showDialog / AppToolbar / FormTitle / WorkflowStepperBar / fmtLocal*) are
// reused verbatim — nothing bespoke is introduced.
// ════════════════════════════════════════════════════════════════════════════

/** Seed passed in from the Sample Prep Queue row.  `prepOid` set = open an
 *  EXISTING batch (read-only view).  `sort` / `matrixId` / `methodId` are
 *  optional queue-row fields consumed by the save body; they default safely
 *  when the queue does not carry them. */
export interface PrepBatchSeed {
  testMethodId: string;               // primary test (drives QC config); = testMethodIds[0]
  // Multi-test Tier-1 batching: all selected tests from the queue (OG "only tier 1
  // allows multiple tests"). Omitted / single element = a single-test batch.
  testMethodIds?: string[];
  tier:         number;
  matrix?:      string;
  test?:        string;
  method?:      string;
  prepOid?:     string | null;
  // Optional queue-row extras (see the save body). Not required by the flow.
  sort?:        number;
  matrixId?:    string | null;
  methodId?:    string | null;
  // Open a NEW batch READ-ONLY (OG: no SamplePreparation Write → View mode). The
  // queue's New sets this when the user lacks write permission.
  viewOnly?:    boolean;
  // Sample-level pre-selection from the Prep Queue "View details" popup (analysis-
  // queue parity): the picked samples' Job Oids pre-fill jobOids, and their
  // SampleLogIn Oids narrow whatever Load returns down to exactly those samples.
  preSelectedJobOids?:         string[];
  preSelectedSampleLogInOids?: string[];
  // Components of the Prep Queue row(s) the batch was opened from (the queue is one row
  // per test + component, 2026-09-30): the Job ID picker, Load and Add Sample offer only
  // those components' samples. The empty GUID = "no component". Absent = every component
  // (a saved batch reopened from the list).
  componentIds?: string[];
}

interface Props {
  seed:   PrepBatchSeed;
  onBack: () => void;
  // "Export To QC Batch" (OG btnExportToQCBatch_Execute): after the local
  // validations pass, hand the saved prep batch to the parent, which swaps this
  // detail for the QC Batch form seeded to auto-import the batch's samples.
  onExportToQc?: (ex: {
    prepOid: string; prepBatchId: string;
    testMethodId: string; test: string | null; method: string | null; matrix: string | null;
  }) => void;
}

// Prep-flow island steps (WorkflowStepperBar) — the FULL 9-step workflow from
// Sample-Prep-Batch-Prototype.html STEP_LABELS (line 861), same as the Sample Prep
// Queue island so the pill is continuous queue→detail. "Select test" + "New" are
// the queue pick-head (shown DONE in the detail); "Others" is optional.
const PREP_STEPS = ['Select test', 'New', 'Job ID', 'Instrument', 'Load',
  'Mark samples', 'Sort', 'Others', 'Create Prep Batch'] as const;

// Every Prep Batch acknowledgement uses the shared centered Tier-1 dialog (same
// convention as the QC twin), never a bespoke toast.
// Routine acknowledgement shown as a centered modal dialog (user preference
// 2026-07-16) via the shared showDialog. (error/warning already auto-route to the
// modal via useNotify.)
const prepMsg = (message: string, title = 'Sample Prep Batch', type: 'success' | 'info' = 'success') =>
  void showDialog({ type, title, message });

// Pull the server's OWN envelope error message off an axios error so the user
// sees the real reason, not axios's generic "Request failed with status …".
function apiErrMsg(e: unknown, fallback: string): string {
  const er = e as { response?: { data?: { error?: { message?: string } } }; isAxiosError?: boolean; message?: string };
  // Twin of the QC form's helper — see the comment there. A CONTROLLED rejection
  // (HTTP 200 + {success:false,error}) reaches us as a plain Error from `unwrap`,
  // which has no `.response`, so without this branch the API's real message was
  // swallowed and replaced by the generic fallback. (fix 2026-08-07)
  const envelope = er.response?.data?.error?.message;
  if (envelope) return envelope;
  if (!er.response && !er.isAxiosError && typeof er.message === 'string' && er.message) return er.message;
  return fallback;
}

// ── local grid row model ─────────────────────────────────────────────────────
type RowKind = 'sample' | 'qc' | 'dilution';
interface PrepRow {
  id: string; kind: RowKind; qc: string; sort: number | null; checked: boolean;
  seqOid?: string | null;
  code: string; job: string; component: string; sid: string;
  // The row's test + method (test-based sequence, 2026-10-01) — display.
  test: string; method: string;
  df: number; finalVol: string;
  dupSource: string; spikeSource: string; sourceId?: string;
  originalSysSampleCode: string | null; isReport: boolean;
  units: string; sampleName: string; siteName: string; samplingAddress: string;
  dateCreated: string | null; dateReceived: string | null; dateCollected: string | null;
  client: string; projectId: string; projectName: string; holdTime: string;
  // prep-specific display columns
  prepMethodOid?: string | null; prepMethod: string;
  // NTU (turbidity) the analyst enters; drives the computed SamplePrep column via
  // the ACT rule for this test/method. Kept as a free-typed string like the other
  // numeric grid cells. (feature 2026-08-14)
  ntu: string;
  prepOptionsOid?: string | null; prepOptions: string;
  functionTypeOid?: string | null; functionType: string;
  datePrepared: string | null; takenSampleUnit?: string | null; multiplier?: number | null;
  // carried for persistence
  qcTypeOid?: string | null; sampleLogInOid?: string | null;
  // The row's component (component-based sequence, 2026-09-30): one row per sample +
  // component; the save links only this component's parameters.
  componentOid?: string | null;
  // The row's test (2026-10-01): one row per sample + test + component; the save links
  // only this test's parameters.
  testMethodOid?: string | null;
  systemId?: string; strSampleId?: string; runno?: number; dilutionCount: number;
  sampleAmount: string; excludedReport: boolean;
}
let uidN = 0;
const uid = () => 'pr' + (++uidN);

/** Identity of a sample ROW: a sample appears once per component (2026-09-30) and once
 *  per test (2026-10-01), so the sample Oid alone no longer identifies a row — its
 *  component and test are part of the key. */
const rowKeyOf = (sampleLogInOid: string | null | undefined, componentOid: string | null | undefined,
                  testMethodOid: string | null | undefined) =>
  `${sampleLogInOid ?? ''}|${(componentOid ?? '').toLowerCase()}|${(testMethodOid ?? '').toLowerCase()}`;

// DatePrepared default for freshly loaded samples: the current local time as the
// naive wire form (shared toWire; ADR-0008 kill-switch → literal local, no shift)
// so the grid shows the real "now". The cell itself edits via the shared
// <InlineCellEditor type="datetime"> (global datetime picker + fmtLocalDateTime).
const datePreparedNow = () => toWire(new Date());

// Columns "Copy To All Cell" can copy (G5 SamplePreparationController allow-list).
type CopyCol = 'sampleAmount' | 'finalVol' | 'datePrepared';
const COPY_COL_LABEL: Record<CopyCol, string> = {
  sampleAmount: 'SampleAmount', finalVol: 'FinalVol', datePrepared: 'DatePrepared',
};

/** DilutionCount editable cell (user 2026-07-15): keeps a local string draft so the
 *  field can be CLEARED and retyped freely (the value isn't force-reset on each
 *  keystroke).  On blur / end-edit, an empty or invalid value defaults back to 1
 *  (the DilutionCount minimum); a valid integer ≥ 1 is committed. */
function DilutionCell({ value, onCommit }: { value: number; onCommit: (n: number) => void }) {
  const [draft, setDraft] = useState(String(value ?? 1));
  useEffect(() => { setDraft(String(value ?? 1)); }, [value]);
  const commit = () => {
    const n = Math.floor(Number(draft));
    const val = Number.isFinite(n) && n >= 1 ? n : 1;   // empty/invalid/<1 → default 1
    setDraft(String(val));
    onCommit(val);
  };
  return (
    <input className="cellinp num" type="number" min={1}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onClick={e => e.stopPropagation()}
      onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      onBlur={commit} />
  );
}

const seqRowToLocal = (r: PrepSeqRow): PrepRow => ({
  id: uid(), kind: r.kind, qc: r.qcType, sort: r.sort, checked: false,
  seqOid: r.seqOid ?? null,
  code: r.code, job: r.jobId ?? '', component: r.component ?? '',
  test: r.test ?? '', method: r.method ?? '',
  // SampleID column binds the SAMPLE LINK only (legacy SamplePrepBatchSequence.SampleID);
  // a standalone QC row (Method Blank, CCB, ICV…) stays BLANK — the old
  // `?? r.strSampleId` fallback showed that row's own run code there. Twin of the
  // QC-form fix. (fix 2026-08-07)
  sid: r.sampleId ?? '',
  df: r.df, finalVol: r.finalVol ?? '',
  dupSource: r.dupSource ?? '', spikeSource: r.spikeSource ?? '', sourceId: r.sourceId ?? undefined,
  originalSysSampleCode: r.originalSysSampleCode ?? null, isReport: r.isReport ?? false,
  units: r.units ?? '', sampleName: r.sampleName ?? '', siteName: r.siteName ?? '',
  samplingAddress: r.samplingAddress ?? '',
  dateCreated: r.dateCreated, dateReceived: r.dateReceived, dateCollected: r.dateCollected,
  client: r.client ?? '', projectId: r.projectId ?? '', projectName: r.projectName ?? '',
  holdTime: r.holdTime ?? '',
  prepMethodOid: r.prepMethodOid, prepMethod: r.prepMethod ?? '',
  ntu: r.ntu != null && r.ntu !== '' ? String(r.ntu) : '',
  prepOptionsOid: r.prepOptionsOid, prepOptions: r.prepOptions ?? '',
  functionTypeOid: r.functionTypeOid, functionType: r.functionType ?? '',
  datePrepared: r.datePrepared, takenSampleUnit: r.takenSampleUnit, multiplier: r.multiplier,
  qcTypeOid: r.qcTypeOid, sampleLogInOid: r.sampleLogInOid, componentOid: r.componentOid ?? null,
  testMethodOid: r.testMethodOid ?? null,
  // No `?? r.code` fallback — OG persists SystemID NULL on Sample rows and the
  // SHORT run code on QC rows; falling back to the full code would corrupt both.
  systemId: r.systemId ?? '', strSampleId: r.strSampleId ?? undefined, runno: r.runno,
  dilutionCount: r.dilutionCount ?? 1, sampleAmount: r.sampleAmount ?? '', excludedReport: r.excludedReport ?? false,
});

const sampleToLocal = (s: PrepSample): PrepRow => ({
  id: uid(), kind: 'sample', qc: 'Sample', sort: 0, checked: false,
  code: s.sysSampleCode ?? '', job: s.jobID ?? '', component: s.component ?? '',
  test: s.test ?? '', method: s.method ?? '',
  sid: s.sysSampleCode ?? '',
  df: 1, finalVol: s.finalVolMl ?? '',
  dupSource: '', spikeSource: '',
  originalSysSampleCode: null, isReport: false,
  units: s.units ?? '', sampleName: s.sampleName ?? '', siteName: s.siteName ?? '',
  samplingAddress: s.samplingAddress ?? '',
  dateCreated: s.dateCreated, dateReceived: s.dateReceived, dateCollected: s.dateCollected,
  client: s.client ?? '', projectId: s.projectID ?? '', projectName: s.projectName ?? '',
  holdTime: s.holdTime ?? '',
  prepMethodOid: s.prepMethodOid, prepMethod: s.prepMethod ?? '',
  ntu: '',
  prepOptionsOid: null, prepOptions: '',
  // FunctionType = SamplePrepType (read-only). DatePrepared defaults to "now"
  // (naive local wire form) and is editable pre-Sort.
  functionTypeOid: null, functionType: s.samplePrepType ?? '',
  datePrepared: datePreparedNow(), takenSampleUnit: null, multiplier: null,
  qcTypeOid: null, sampleLogInOid: s.sampleLogInOid, componentOid: s.componentOid ?? null,
  testMethodOid: s.testMethodOid ?? null,
  // SystemID stays empty on Sample rows (OG persists NULL — live-data verified).
  systemId: '', strSampleId: s.sysSampleCode ?? '', runno: 1,
  dilutionCount: s.dilutionCount > 0 ? s.dilutionCount : 1, sampleAmount: '', excludedReport: s.excludedReport,
});

// Local grid row → wire shape, shared by Sort/Add QC/Remove QC/Save (all POST
// the CURRENT full grid to the server).
const toWireRow = (r: PrepRow): PrepSeqRow => ({
  seqOid: r.seqOid ?? null,
  kind: r.kind, qcType: r.qc, qcTypeOid: r.qcTypeOid ?? null,
  sort: r.sort ?? 0, runno: r.runno ?? 1, code: r.code, systemId: r.systemId || null,
  jobId: r.job || null, component: r.component || null,
  strSampleId: r.strSampleId ?? null, sampleId: r.sid || null,
  sampleLogInOid: r.sampleLogInOid ?? null, componentOid: r.componentOid ?? null,
  testMethodOid: r.testMethodOid ?? null, test: r.test || null, method: r.method || null, df: r.df,
  sampleAmount: r.sampleAmount || null, finalVol: r.finalVol || null,
  dupSource: r.dupSource || null, spikeSource: r.spikeSource || null,
  sourceId: r.sourceId ?? null,
  originalSysSampleCode: r.originalSysSampleCode ?? null, isReport: r.isReport ?? false,
  dilutionCount: r.dilutionCount, excludedReport: r.excludedReport,
  units: r.units || null, sampleName: r.sampleName || null,
  siteName: r.siteName || null, samplingAddress: r.samplingAddress || null,
  dateCreated: r.dateCreated, dateReceived: r.dateReceived, dateCollected: r.dateCollected,
  client: r.client || null, projectId: r.projectId || null,
  projectName: r.projectName || null, holdTime: r.holdTime || null,
  prepMethodOid: r.prepMethodOid ?? null, prepMethod: r.prepMethod || null,
  ntu: r.ntu?.trim() && Number.isFinite(Number(r.ntu)) ? Number(r.ntu.trim()) : null,
  prepOptionsOid: r.prepOptionsOid ?? null, prepOptions: r.prepOptions || null,
  functionTypeOid: r.functionTypeOid ?? null, functionType: r.functionType || null,
  datePrepared: r.datePrepared, takenSampleUnit: r.takenSampleUnit ?? null, multiplier: r.multiplier ?? null,
});

/** The ';'-joined instrument Oids OG stores in SamplePrepBatch.Instrument, as a list. */
const splitInstrOids = (joined: string | null | undefined): string[] =>
  (joined ?? '').split(';').map(s => s.trim()).filter(Boolean);

// Signature of the edit-mode savable state (sequence + Temp/Humidity/Comment/
// Instrument — exactly what saveEdits→updateBatch persists). Drives the OK
// dirty-gate: OK is enabled only once this changes vs the loaded baseline.
// Twin of QcBatchDetailPage.editSig.
const editSig = (rows: PrepRow[], temperature: string, humidity: string, comment: string, instrument: string | null): string =>
  JSON.stringify({ s: rows.map(toWireRow), t: temperature, h: humidity, c: comment, i: instrument });

export default function PrepBatchDetailPage({ seed, onBack, onExportToQc }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();
  // Current user's display name — the pre-save 'Created By' placeholder shows the
  // actual logged-in user (twin of QcBatchDetailPage item C, 2026-09-01) instead of
  // hard-coding 'Service'. A saved header's real Employee DisplayName still wins.
  const meName = useAuthStore(s => s.user?.name || s.user?.email || '');

  // Test/Method/Matrix/Tier — from the seed for a new batch; overwritten from
  // the header once an existing batch's detail loads.
  // Stateful so an EXISTING batch (opened from the list — seed.testMethodId is '')
  // resolves its real test method from the loaded header; otherwise the reference
  // load (jobs / instruments / QC types / prep options) is skipped and the QCType
  // panel stays empty.
  const [effTestMethodId, setEffTestMethodId] = useState(seed.testMethodId);
  // All selected test methods (multi-test Tier-1 batch). Jobs / Instruments / Load /
  // Save span every test; the QC-interleave config (Sort/Add-QC) keeps the primary.
  // Stateful + empties filtered: an EXISTING batch seeds testMethodId '' → this would
  // otherwise be [''], and Add Sample (POST /load) sends it as Guid[] → '' fails to
  // bind → 500 "Failed to load candidate samples". loadDetail resolves the real list.
  const [effTestMethodIds, setEffTestMethodIds] = useState<string[]>(
    seed.testMethodIds && seed.testMethodIds.length > 0
      ? seed.testMethodIds
      : (seed.testMethodId ? [seed.testMethodId] : []));
  const [effTest, setEffTest]     = useState<string | null>(seed.test ?? null);
  const [effMethod, setEffMethod] = useState<string | null>(seed.method ?? null);
  const [effMatrix, setEffMatrix] = useState<string | null>(seed.matrix ?? null);
  const [tier, setTier]           = useState<number>(seed.tier);
  // Prep sort (queue "PB-<tier>-<sort>") — the queue row can pass it; default 0.
  const sort = seed.sort ?? 0;

  // Read-only when opening an existing batch, OR a NEW batch the user lacks write
  // permission for (seed.viewOnly — OG SamplePreparation no-Write → View mode).
  const [viewOnly, setViewOnly] = useState<boolean>(!!seed.prepOid || !!seed.viewOnly);
  const [existingHeader, setExistingHeader] = useState<PrepHeader | null>(null);
  // Component scope: the Prep Queue row's component(s) for a new batch; for a reopened
  // saved batch, the components its linked parameters carry (header.componentIds) — the
  // batch table has no component column (2026-09-30). Undefined = the whole test.
  const componentIds = seed.componentIds
    ?? (existingHeader?.componentIds ? existingHeader.componentIds.split(',').filter(Boolean) : undefined);

  // ── reference data ──
  const [jobsList, setJobsList]   = useState<QcJob[]>([]);
  const [instrList, setInstrList] = useState<QcInstrument[]>([]);
  const [qcTypeList, setQcTypeList] = useState<QcTypeOption[]>([]);
  // PrepOptions combo list — the Settings ▸ Utility ▸ Prep Options master (dbo.PrepOptions).
  const [prepOptionsList, setPrepOptionsList] = useState<{ oid: string; prepName: string | null }[]>([]);
  const [loadedSamples, setLoadedSamples] = useState<PrepSample[]>([]);

  // ── selections / form state ──
  const [jobOids, setJobOids]       = useState<string[]>(seed.preSelectedJobOids ?? []);
  // Sample-level narrowing from the Prep Queue popup (null = no narrowing → Load
  // returns the job's full pending-prep list, as before).
  const [preSelectedSlOids] = useState<string[] | null>(seed.preSelectedSampleLogInOids ?? null);
  const [instrument, setInstrument] = useState<string | null>(null);
  const [loaded, setLoaded]         = useState(false);
  const [sorted, setSorted]         = useState(false);
  const [rows, setRows]             = useState<PrepRow[]>([]);
  const [tab, setTab]               = useState<'seq' | 'others'>('seq');
  const [openPop, setOpenPop]       = useState<'job' | 'instr' | null>(null);
  const [giCollapsed, setGiCollapsed] = useState(false);
  const [qcCollapsed, setQcCollapsed] = useState(false);
  const [fullGrid, setFullGrid]     = useState(false);

  // Full-page drill-down from the Sample Prep Queue: collapse the side nav for room
  // and set the top-bar title to this form's name (the routed page is still "Sample
  // Prep Queue"). Both revert on unmount. (User UI feedback 2026-09-16 #1 + #5.)
  useEffect(() => {
    window.dispatchEvent(new Event('ai:nav-collapse'));
    window.dispatchEvent(new CustomEvent('ai:topbar-title', { detail: 'Sample Prep Batch' }));
    return () => {
      window.dispatchEvent(new Event('ai:nav-restore'));
      window.dispatchEvent(new CustomEvent('ai:topbar-title', { detail: null }));
    };
  }, []);
  const [temperature, setTemperature] = useState('');
  const [humidity, setHumidity]     = useState('');
  const [comment, setComment]       = useState('');
  // Edit-mode OK dirty-gate (QC-form parity): OK is enabled only once the loaded
  // batch actually changed. `editBaseline` = the just-loaded savable signature
  // (captured in loadDetail); `tabsDirty` flags an Others-tab (reagent/instrument)
  // edit. Both reset on (re)load.
  const editBaseline = useRef<string | null>(null);
  const [tabsDirty, setTabsDirty] = useState(false);
  const [savedPrepId, setSavedPrepId] = useState<string | null>(null);
  const [modal, setModal] = useState<null | 'notes' | 'instr2' | 'reagent' | 'addsample' | 'copy'>(null);

  // ── Prep-sequence grid column model (client 2026-08-05: RESIZE + Hide / Show All /
  // Column Chooser; no column reorder). visibleColumns drives BOTH header and body so
  // a hidden column drops from both. RUNTIME-ONLY (user 2026-09-28, "like XAF runtime"):
  // NO persistKey, so hidden columns + dragged widths last while the form is open and
  // every reopen starts from the full default layout. Since 2026-09-28 the 4
  // frozen identity columns are in the model too (XAF parity — ANY column can be
  // hidden at runtime; twin of the QC form); they stay pinned, a hidden one just
  // collapses its sticky offset (frozenWidthVars). Resize stays data-only.
  const PREP_DATA_COLS = useMemo(() => [
    { key: 'qc',              label: 'QCType',        frozen: true },
    { key: 'sort',            label: 'Sort',          frozen: true },
    { key: 'code',            label: 'SysSamplecode', frozen: true },
    { key: 'job',             label: 'JobID',         frozen: true },
    { key: 'dilution',        label: sorted ? 'DF' : 'DilutionCount' },
    { key: 'sampleAmount',    label: 'SampleAmount' },
    { key: 'finalVol',        label: 'FinalVol(ml)' },
    { key: 'dupSource',       label: 'DupSource' },
    { key: 'spikeSource',     label: 'SpikeSource' },
    // Order per user feedback 2026-09-29: Component · NTU · PreOption right after the
    // editable columns; the read-only SampleName…PrepMethod block moved right, before Client.
    // Test · Method before Component (user 2026-10-01): one row per sample + test + component.
    { key: 'test',            label: 'Test' },
    { key: 'method',          label: 'Method' },
    { key: 'component',       label: 'Component' },
    { key: 'ntu',             label: 'NTU' },
    // PrepOptions (OG PrepNameOptions) — grid caption "PreOption" (user 2026-09-29).
    { key: 'prepOptions',     label: 'PreOption' },
    { key: 'samplePrep',      label: 'SamplePrep' },
    { key: 'functionType',    label: 'FunctionType' },
    { key: 'holdTime',        label: 'HoldTime' },
    { key: 'datePrepared',    label: 'DatePrepared' },
    { key: 'dateReceived',    label: 'DateReceived' },
    { key: 'dateCollected',   label: 'DateCollected' },
    { key: 'sampleName',      label: 'SampleName' },
    { key: 'siteName',        label: 'SiteName' },
    { key: 'samplingAddress', label: 'SamplingAddress' },
    { key: 'units',           label: 'Units' },
    { key: 'prepMethod',      label: 'PrepMethod' },
    { key: 'client',          label: 'Client' },
    { key: 'projectId',       label: 'ProjectID' },
  ], [sorted]);
  const seqCm = useColumnManager(PREP_DATA_COLS, c => c.key, c => c.label);
  // Scrolling data columns (the frozen block is rendered separately) + frozen visibility.
  const seqDataCols = seqCm.visibleColumns.filter(c => !c.frozen);
  const fzShown = (k: string) => !seqCm.hidden.has(k);
  const fzLast = [...seqCm.visibleColumns].reverse().find(c => c.frozen)?.key;
  const fzEdge = (k: string) => (k === fzLast ? ' fzedge' : '');
  const seqW = (k: string) => {
    const w = seqCm.colWidth(k);
    return w ? { width: w, minWidth: w, maxWidth: w } : {};
  };
  // ── DupSource / SpikeSource selection combos ────────────────────────────────
  // Legacy prep has these too (SamplePreparationController :2368/:3455) with a rule
  // byte-identical to the QC form's, so both call the ONE shared builder in
  // ./dupSpikeSource. Recomputed from the LIVE grid so add/remove/renumber re-narrows
  // every dropdown; a row absent from the map has no valid sources → plain text.
  const sourceOpts = useMemo(
    () => buildSourceOptions(rows, qcTypeList),
    [rows, qcTypeList]);

  // Keep stored Dup/Spike sources valid as rows come and go — twin of the QC form's
  // reconcile (user 2026-08-07): a now-invalid value adopts the only remaining
  // candidate, or clears when there are several. Only writes on a real change.
  useEffect(() => {
    if (!sorted) return;
    const fixes = reconcileSources(rows, sourceOpts);
    if (fixes.length === 0) return;
    const byId = new Map(fixes.map(f => [f.id, f]));
    setRows(prev => prev.map(r => {
      const f = byId.get(r.id);
      return f ? { ...r, ...f, id: r.id } : r;
    }));
  }, [rows, sorted, sourceOpts]);

  // ── "Export to" (legacy XAF default ExportController above the sequence grid) ──
  // Twin of the QC Batch form's export — the SAME shared <ExportMenu> and the same
  // 10 ALPACA formats (rule 8).  Prep's frozen data columns are QCType · Sort ·
  // SysSamplecode · JobID (Component is a normal data column here), then the CURRENTLY VISIBLE
  // data columns, so Hide / Column Chooser and the DilutionCount ⇄ DF caption swap
  // carry into the file.  Cell text mirrors the <td> expressions (dates via the
  // shared fmtLocal* helper §14b.6; JobID blank on non-Sample rows, OG parity).
  const seqExportText = (r: PrepRow, key: string): string => {
    switch (key) {
      case 'dilution':      return String((sorted ? r.df : r.dilutionCount) || '');
      case 'dupSource':     return sorted ? (r.dupSource || '') : '';
      case 'spikeSource':   return sorted ? (r.spikeSource || '') : '';
      case 'datePrepared':  return fmtLocalShort(r.datePrepared);
      case 'dateReceived':  return fmtLocalShort(r.dateReceived);
      case 'dateCollected': return fmtLocalShort(r.dateCollected);
      case 'samplePrep':    return samplePrepFromNtu(r.ntu);  // computed, not a row field
      default:              return String((r as unknown as Record<string, unknown>)[key] ?? '');
    }
  };
  // ── View-only sort + Excel-style filter on the sequence grid — twin of the QC
  // form (user 2026-09-28, shared useSeqGridView). `rows` (the saved / exported /
  // renumbered run order) is never reordered; while a sort/filter is on, the
  // position-based actions (drag, Remove, Add QC, Add Dilution, pre-Sort select-all)
  // are locked. A rebuilt sequence (Sort / Previous) resets the view. ──
  const seqViewText = (r: PrepRow, key: string): string => {
    switch (key) {
      case 'qc':   return r.qc || '';
      case 'sort': return r.sort == null ? '' : String(r.sort);
      case 'code': return r.code || '';
      case 'job':  return r.qc === 'Sample' ? (r.job || '') : '';
      default:     return seqExportText(r, key);
    }
  };
  const seqView = useSeqGridView(rows, {
    text: seqViewText,
    sortValue: (r, key) => {
      switch (key) {
        case 'sort':          return r.sort;
        case 'datePrepared':  return r.datePrepared;
        case 'dateReceived':  return r.dateReceived;
        case 'dateCollected': return r.dateCollected;
        default:              return seqViewText(r, key);
      }
    },
    keys: seqCm.visibleColumns.map(c => c.key),
    resetOn: sorted,
  });
  const viewLocked = seqView.active;
  // Header right-click menu → Sort · Hide / Show All / Column Chooser. Portals to body; rendered once.
  const seqMenu = useHeaderMenu(seqView.menuSort, seqCm.columnMenu);
  // Export = the FULL run order (never the sorted/filtered view), user 2026-09-28 —
  // with exactly the VISIBLE columns (frozen + data), so a runtime Hide carries into the file.
  const seqExport = useMemo(() => ({
    headers: seqCm.visibleColumns.map(c => c.label),
    rows: rows.map(r => seqCm.visibleColumns.map(c => seqViewText(r, c.key))),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seqViewText is a pure
    // formatter recreated each render; the data it reads is covered by rows/sorted.
  }), [rows, sorted, seqCm.visibleColumns]);
  const seqHClass: Record<string, string> = {
    dilution:     `num ${sorted ? 'col-df' : 'col-num-sm'}`,
    sampleAmount: 'num col-num-sm',
    finalVol:     'num',
    dupSource:    'col-dupspike',
    spikeSource:  'col-dupspike',
  };
  // Add Sample — pending-prep candidates fetched on demand, shown in the picker.
  const [addSampleCandidates, setAddSampleCandidates] = useState<PrepSample[]>([]);

  // ── Others tab: Reagents (legacy ReagentLink_Execute parity — mirrors the QC
  // twin).  POST-save rows are server-loaded once savedPrepId is set; PRE-save
  // picks are staged locally and shipped atomically with Create. ─────────────
  const [reagentRows, setReagentRows] = useState<QcLinkedReagent[]>([]);
  const [reagentSel, setReagentSel]   = useState<Set<string>>(new Set());
  const [stagedReagents, setStagedReagents] = useState<QcAvailableReagent[]>([]);
  const [stagedReagentSel, setStagedReagentSel] = useState<Set<string>>(new Set());
  // Others tab: Instruments (legacy InstrumentLink_Execute parity) — same
  // saved-vs-staged split as reagents.
  const [instrumentRows, setInstrumentRows] = useState<QcInstrument[]>([]);
  const [instrumentSel, setInstrumentSel]   = useState<Set<string>>(new Set());
  const [stagedInstruments, setStagedInstruments] = useState<QcInstrument[]>([]);
  const [stagedInstrumentSel, setStagedInstrumentSel] = useState<Set<string>>(new Set());

  // ── load reference data for the (known) test method ──
  useEffect(() => {
    if (!effTestMethodId) return;
    let cancelled = false;
    (async () => {
      try {
        const [j, i, q] = await Promise.all([
          prepBatchApi.jobs(effTestMethodId, sort, tier, effTestMethodIds, seed.componentIds),
          prepBatchApi.instruments(effTestMethodId, effTestMethodIds),
          prepBatchApi.qcTypes(effTestMethodId),
        ]);
        if (cancelled) return;
        setJobsList(j); setInstrList(i); setQcTypeList(q);
      } catch {
        if (!cancelled) notify.error('Failed to load prep batch reference data — check the API logs.');
      }
    })();
    return () => { cancelled = true; };
  }, [effTestMethodId, sort, tier, notify]);

  // PrepOptions combo list (restored 2026-09-28) — global, not test-scoped; loaded once.
  useEffect(() => {
    let cancelled = false;
    prepBatchApi.prepOptions()
      .then(po => { if (!cancelled) setPrepOptionsList(po); })
      .catch(() => { if (!cancelled) notify.error('Failed to load the Prep Options list.'); });
    return () => { cancelled = true; };
  }, [notify]);

  // Project Notes count for the toolbar caption — job-scoped, same shared feed
  // the NotesModal reads.
  const [notesCount, setNotesCount] = useState<number | null>(null);
  const loadNotesCount = useCallback(async () => {
    const job = jobOids[0];
    if (!job) { setNotesCount(null); return; }
    try {
      const r = await api.get(`/api/v1/reporting/custom/${encodeURIComponent(job)}/project-notes`,
        { silentLoading: true } as never);
      setNotesCount(((r.data as { data?: unknown[] }).data ?? []).length);
    } catch { setNotesCount(null); }
  }, [jobOids]);
  useEffect(() => { void loadNotesCount(); }, [loadNotesCount]);

  // Open an existing prep batch (view mode) — header + built sequence.
  const loadDetail = useCallback(async (prepOid: string) => {
    const { header, sequence } = await prepBatchApi.getDetail(prepOid);
    setExistingHeader(header);
    // Resolve the real test method (header.testMethodId may be a ';'-joined list for
    // a multi-test batch — the QCType panel keys off the PRIMARY test) so the
    // reference load runs and the QCType panel populates.
    const testList = header.testMethodId?.split(';').map(s => s.trim()).filter(Boolean) ?? [];
    if (testList.length > 0) { setEffTestMethodId(testList[0]!); setEffTestMethodIds(testList); }
    setEffTest(header.test); setEffMethod(header.method); setEffMatrix(header.matrix);
    if (header.tier != null) setTier(header.tier);
    setTemperature(header.temperature ?? '');
    setHumidity(header.humidity ?? '');
    setComment(header.comments ?? '');
    setInstrument(header.instrumentOids);
    setJobOids(header.jobOids ? header.jobOids.split(';').filter(Boolean) : []);
    const localRows = sequence.map(seqRowToLocal);
    setRows(localRows);
    setLoaded(true); setSorted(true);
    setSavedPrepId(header.prepBatchOid);
    // Baseline for the OK dirty-gate: the just-loaded savable state (nothing to save
    // until this signature changes). tabsDirty resets too.
    editBaseline.current = editSig(localRows, header.temperature ?? '', header.humidity ?? '', header.comments ?? '', header.instrumentOids);
    setTabsDirty(false);
    // A saved batch opens EDITABLE (A1 — mirror the QC form's open-existing→edit),
    // UNLESS the caller asked for VIEW mode (row-click, seed.viewOnly) or the user
    // has no write permission. A ResultCompleted batch (status 3) always stays
    // read-only (same gate the update SP enforces + the Delete gate). This preserves
    // the QC-form distinction: row-click → view, Edit → edit.
    setViewOnly(seed.viewOnly === true || header.status === 3);
  }, []);

  useEffect(() => {
    if (!seed.prepOid) return;
    let cancelled = false;
    (async () => {
      try { if (!cancelled) await loadDetail(seed.prepOid!); }
      catch { if (!cancelled) notify.error('Failed to load the prep batch — check the API logs.'); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed.prepOid]);

  // Prep Queue "View details" → OK: the analyst ticked their samples in the popup,
  // so land them in the grid immediately (analysis-queue → QC-form parity — the
  // picked samples "load" on OK, no manual Load click). New batch only; runs once.
  // The instrument is still required before Sort/OK, so this uses force=true to
  // bypass only the Load-button's instrument gate (the load API doesn't need it).
  const autoLoadedFromQueue = useRef(false);
  useEffect(() => {
    if (autoLoadedFromQueue.current) return;
    if (seed.prepOid) return;                                   // existing batch → loadDetail
    if (!preSelectedSlOids || preSelectedSlOids.length === 0) return;
    if (!effTestMethodId || jobOids.length === 0) return;
    autoLoadedFromQueue.current = true;
    void loadSamples({ force: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effTestMethodId, jobOids, preSelectedSlOids, seed.prepOid]);

  // ── stepper status ──
  const stepStatus = useMemo<StepState[]>(() => {
    // Prototype stepStatus() (Sample-Prep-Batch-Prototype.html :866) for the DETAIL
    // screen: Select test(0)+New(1) done; Job ID(2)→Instrument(3)→Load(4)→Mark(5)→
    // Sort(6) progress; Others(7) optional (done once records/saved); Create Prep
    // Batch(8) active after Sort, done once saved.
    const st: StepState[] = new Array(PREP_STEPS.length).fill('todo');
    st[0] = 'done'; st[1] = 'done';                               // Select test, New
    const jobOk = jobOids.length > 0;
    st[2] = jobOk ? 'done' : 'active';                            // Job ID
    if (jobOk) st[3] = instrument ? 'done' : 'active';            // Instrument
    if (jobOk && instrument) st[4] = loaded ? 'done' : 'active';  // Load
    if (loaded) {
      // Once Sort has run the checkboxes are cleared, so `marked` alone would flip
      // step 6 back to incomplete — treat `sorted` as proof the samples were marked.
      const marked = sorted || rows.some(r => r.checked);
      st[5] = marked ? 'done' : 'active';                         // Mark samples
      if (marked) st[6] = sorted ? 'done' : 'active';             // Sort
    }
    if (sorted) { st[5] = 'done'; st[6] = 'done'; st[7] = 'optional'; st[8] = 'active'; }  // Mark+Sort done, Others optional, Create active
    if (savedPrepId) { st[7] = 'done'; st[8] = 'done'; }
    return st;
  }, [jobOids, instrument, loaded, sorted, rows, savedPrepId]);

  const renumber = (arr: PrepRow[]) => arr.map((r, i) => ({ ...r, sort: i }));

  // Patch one grid row (pre-Sort inline edits: DilutionCount / SampleAmount /
  // FinalVol / PrepOptions / DatePrepared). Edits persist in `rows` and survive
  // Sort (see buildSequence) and Save (toWireRow carries every prep field).
  const updateRow = (id: string, patch: Partial<PrepRow>) =>
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
  // OG SamplePrepLoad_Execute (:4583) gates Load on Job+Matrix+Test+Method only —
  // NOT Instrument (instrument is optional at Load). effTestMethodId already covers
  // matrix/test/method, so Job selection is the effective gate.
  const loadEnabled = jobOids.length > 0;

  // OG locks EVERY header field once the batch is sorted (`enbstat=false` when the
  // Sort action has flipped to "Ok" — OnViewControlsCreated :1638-1641), so Temp /
  // Humidity must be entered BEFORE Sort; "Previous" unlocks them again. Job ID and
  // Instrument already carry `sorted` in their own pickers. Scoped to the CREATE
  // flow: an existing saved batch stays editable, which is OG's History-view save
  // (SavePrepBatch_Execute) and AI's edit-existing feature.
  const headerLocked = viewOnly || (sorted && !existingHeader);
  // Export To QC Batch shown in the detail toolbar (OG enables it on saved batches;
  // re-enabled 2026-07-25). runExportToQc gates it to a saved batch at the right status.
  const showExportToQc = true;

  // OK dirty-gate (QC-form parity): true once the sequence / Temp / Humidity /
  // Comment / Instrument changed vs the loaded baseline, or an Others-tab
  // (reagent / instrument) edit was made. Drives the edit-mode OK button.
  const editDirty = useMemo(
    () => tabsDirty || (editBaseline.current !== null
      && editSig(rows, temperature, humidity, comment, instrument) !== editBaseline.current),
    [tabsDirty, rows, temperature, humidity, comment, instrument]);

  // Unsaved-changes guard (navigate-away warning). A NEW batch is dirty once
  // samples are loaded or reagents/instruments are staged; a SAVED batch once an
  // edit is made (editDirty). Registers with the global guard + beforeunload.
  const guardDirty = savedPrepId == null
    ? (loaded || rows.length > 0 || stagedReagents.length > 0 || stagedInstruments.length > 0)
    : editDirty;
  useUnsavedGuard(guardDirty);
  const guardedBack = async () => { if (await confirmDiscardIfUnsaved()) onBack(); };

  // ── picker labels ──
  const jobLabel = existingHeader?.jobIds
    ? existingHeader.jobIds
    : jobOids.length
      ? jobsList.filter(j => jobOids.includes(j.jobOid)).map(j => j.jobID).join(';')
      : 'Select job(s)…';
  // Instrument is MULTI-select, as in OG (SamplePrepBatch.Instrument is a checked list;
  // SamplePreparationController GridLookup_ValueChanged :2962 stores the Oids joined by ';'
  // and strInstrument the names joined by ';'). `instrument` holds that joined Oid string.
  const instrOids = splitInstrOids(instrument);
  // Names in the same ';' form OG writes to strInstrument; null when one isn't in the list.
  const instrNames = (): string | null => {
    const names = instrOids.map(o => instrList.find(x => x.instrumentOid === o)?.instrumentName);
    return names.length > 0 && names.every(Boolean) ? names.join(';') : null;
  };
  const instrLabel = instrument
    ? (instrNames()?.replace(/;/g, '; ') ?? existingHeader?.instrumentName ?? instrument)
    : 'Select instrument…';

  // ── actions ──
  function toggleJob(oid: string) {
    setJobOids(prev => (prev.includes(oid) ? prev.filter(x => x !== oid) : [...prev, oid]));
    if (loaded) { setLoaded(false); setSorted(false); setRows([]); setLoadedSamples([]); }
  }
  // Toggle one instrument; the list stays open for more (Apply closes it). Kept in the
  // list's order, as OG's lookup returns its selected rows in grid order.
  function pickInstr(oid: string) {
    setInstrument(prev => {
      const cur = splitInstrOids(prev);
      const next = cur.includes(oid) ? cur.filter(o => o !== oid) : [...cur, oid];
      const ordered = instrList.map(i => i.instrumentOid).filter(o => next.includes(o));
      const rest = next.filter(o => !ordered.includes(o));
      const all = [...ordered, ...rest];
      return all.length ? all.join(';') : null;
    });
  }

  // `force` bypasses the Load-button gate (which also requires an Instrument) so
  // the Prep Queue "View details" → OK auto-load can populate the grid before the
  // analyst picks an instrument. The load API itself doesn't need the instrument
  // (it's a UI gate only); the instrument is still required before Sort/OK/Save.
  async function loadSamples(opts?: { force?: boolean }) {
    if (!opts?.force && !loadEnabled) return;
    try {
      let samples = await prepBatchApi.load({ testMethodId: effTestMethodId, testMethodIds: effTestMethodIds, jobOids, sort, tier,
        componentIds });
      // Prep Queue "View details" popup parity (analysis-queue → QC flow): narrow
      // the job's full pending list down to exactly the samples the user ticked.
      if (preSelectedSlOids && preSelectedSlOids.length > 0) {
        const keep = new Set(preSelectedSlOids);
        samples = samples.filter(s => keep.has(s.sampleLogInOid));
      }
      setLoadedSamples(samples);
      setRows(samples.map(sampleToLocal));
      setLoaded(true); setSorted(false);
      if (samples.length === 0) notify.warning('No pending samples found for the selected job(s).');
    } catch (e) {
      notify.error(apiErrMsg(e, 'Failed to load samples.'));
    }
  }

  function resetBatch() {
    setJobOids([]); setInstrument(null);
    setLoaded(false); setSorted(false); setRows([]); setLoadedSamples([]);
    setTab('seq');
  }

  // ── Add Sample (OG SamplePreparationController.AddSample_Execute :6839) ───────
  // OG opens a popup ListView of pending-prep candidates for the batch's
  // test/matrix/method ACROSS ALL JOBS, EXCLUDING SampleLogIns already in the
  // sequence, then Dc_Accepting (:6882) appends each pick as a new "Sample"
  // sequence row stamped with the same PrepMethod/HoldTime/Units as Load. AI
  // matches OG's cross-all-jobs scope with `allJobs` (the Load SP then ignores the
  // job list) — NOT by borrowing the Job ID picker's list, which is division-scoped
  // since 2026-09-28 while OG's Add Sample has no division filter. The stamping is
  // identical — the Load SP resolves PrepMethod/SamplePrepType/Units per candidate,
  // so sampleToLocal yields fully-stamped rows that match the rest of the batch.
  async function openAddSample() {
    // Rows already in the sequence, by sample + component + test: a sample whose other
    // component / test isn't in the batch yet can still be added (2026-09-30 / 2026-10-01).
    const used = new Set(rows
      .filter(r => r.kind === 'sample' && r.sampleLogInOid)
      .map(r => rowKeyOf(r.sampleLogInOid, r.componentOid, r.testMethodOid)));
    try {
      const all = await prepBatchApi.load({
        testMethodId: effTestMethodId, testMethodIds: effTestMethodIds, jobOids: [], sort, tier,
        allJobs: true,
        // Same component(s) as the batch — the queue row's, or a reopened batch's own.
        componentIds,
      });
      const candidates = all.filter(c => !used.has(rowKeyOf(c.sampleLogInOid, c.componentOid, c.testMethodOid)));
      if (candidates.length === 0) { notify.warning('No more samples available to add.'); return; }
      setAddSampleCandidates(candidates);
      setModal('addsample');
    } catch (e) {
      notify.error(apiErrMsg(e, 'Failed to load candidate samples.'));
    }
  }

  // Picker OK — append the picked candidates as new Sample rows. Pre-Sort they join
  // the loadedSamples pool (buildSequence sources the Sort from it by SysSampleCode)
  // and the sample list; post-Sort they land at the end of the run sequence and are
  // renumbered so they flow through toWireRow → Save unchanged.
  function commitAddSample(selected: PrepSample[]) {
    setModal(null);
    if (selected.length === 0) return;
    const newRows = selected.map(sampleToLocal);
    setRows(prev => (sorted ? renumber([...prev, ...newRows]) : [...prev, ...newRows]));
    if (!sorted) setLoadedSamples(prev => [...prev, ...selected]);
    prepMsg(`${selected.length} sample(s) added.`, 'Add Sample');
  }

  // Sort → server-side QC interleave from the CHECKED loaded samples.
  async function buildSequence() {
    const sampleRows = rows.filter(r => r.kind === 'sample');
    const checkedCount = sampleRows.filter(r => r.checked).length;
    if (checkedCount === 0) { notify.warning('Select at least one sample (tick its checkbox) before Sort.'); return; }
    // Every SELECTED sample must carry a Sort value — OG SamplePrepSort_Execute
    // (:4997-5003), verbatim twin of the QC form's guard (QCBatchController :6953).
    // (fix 2026-08-07)
    const checkedRows = sampleRows.filter(r => r.checked);
    if (checkedRows.some(r => (r.sort ?? 0) <= 0)) {
      notify.warning('Enter the sort value for selected samples');
      return;
    }
    // Build the payload in the analyst's SORT ORDER — OG AddSequence takes the sample
    // list as `…Where(x => x.SampleID != null).OrderBy(a => a.Sort)`
    // (SamplePreparationController.cs:7048). Previously this filtered `loadedSamples`
    // (the SP's load order), silently discarding a deliberate out-of-order selection.
    // Twin of the QC-form fix. (fix 2026-08-07)
    // Keyed by sample + component + test — a sample's rows share one code (2026-09-30).
    const loadedByKey = new Map(loadedSamples.map(s => [rowKeyOf(s.sampleLogInOid, s.componentOid, s.testMethodOid), s]));
    const toSort = checkedRows
      .slice()
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
      .flatMap(r => { const s = loadedByKey.get(rowKeyOf(r.sampleLogInOid, r.componentOid, r.testMethodOid)); return s ? [s] : []; });
    if (toSort.length === 0) { notify.warning('Select at least one sample (tick its checkbox) before Sort.'); return; }
    // OG SamplePrepSort_Execute (:5117-5130) requires an Instrument at Sort. If none
    // is selected: warn to pick one when the test HAS linked instruments, else block
    // with the "not linked to this test" message (verbatim OG). Instrument is NOT
    // required to Load — only to Sort.
    if (!instrument) {
      if (instrList.length > 0) notify.warning('Please select an instrument.');
      else notify.error('Instrument has not been linked to this test. Get assistance from your LIMS administrator to add the instruments.');
      return;
    }
    // Snapshot the pre-Sort inline edits per sample — the build endpoint maps
    // samples to QcBatchSampleRow, which DROPS the prep-only fields, so re-apply
    // them onto the built rows afterwards (keyed by sampleLogInOid).
    const editByOid = new Map(sampleRows
      .filter(r => r.sampleLogInOid)
      .map(r => [rowKeyOf(r.sampleLogInOid, r.componentOid, r.testMethodOid), {
        dilutionCount: r.dilutionCount, sampleAmount: r.sampleAmount, finalVol: r.finalVol,
        prepOptionsOid: r.prepOptionsOid, prepOptions: r.prepOptions,
        functionTypeOid: r.functionTypeOid, functionType: r.functionType, datePrepared: r.datePrepared,
        ntu: r.ntu,   // analyst-entered turbidity survives Sort (feature 2026-08-14)
      }] as const));
    // A prep batch has ONE prep method (the tier's) — the build drops PrepMethod
    // (QcBatchSampleRow has no such field), so stamp EVERY built row with the
    // common method afterwards. OG parity: QC rows (CCB/ICV/…) carry the anchor
    // sample's PrepMethod too, not just the sample rows.
    // OG SamplePreparationController AddQC (:5322-5331) copies the anchor sample's
    // Units / HoldTime / PrepMethod / FunctionType / DatePrepared onto a standalone
    // QC row, so OG shows them on ALL rows. These are batch/test/method-level
    // (identical for every sample of the batch), so sampleRows[0] is a valid common
    // source — same pattern as PrepMethod. The shared QcSequenceBuilder.MakeLeadQc /
    // MakeSampleQc deliberately leave them blank on QC rows (QC-form rule); we
    // re-stamp here in the PREP grid only, never touching the shared builder.
    // (Sample-Prep column parity, 2026-07-15)
    const commonPrepMethod     = sampleRows[0]?.prepMethod ?? '';
    const commonPrepMethodOid  = sampleRows[0]?.prepMethodOid ?? null;
    const commonUnits          = sampleRows[0]?.units ?? '';
    const commonHoldTime       = sampleRows[0]?.holdTime ?? '';
    const commonFunctionType   = sampleRows[0]?.functionType ?? '';
    const commonFunctionTypeOid= sampleRows[0]?.functionTypeOid ?? null;
    const commonDatePrepared   = sampleRows[0]?.datePrepared ?? null;
    // PrepOptions (PrepNameOptions) is a PER-SAMPLE user selection, not a batch
    // constant — OG copies the ANCHOR sample's value onto QC rows (ctrl :5330).
    // So stamp it onto QC rows ONLY (from the anchor sample), and leave each
    // sample row's own selection (including a deliberate blank) untouched.
    const commonPrepOptions    = sampleRows[0]?.prepOptions ?? '';
    const commonPrepOptionsOid = sampleRows[0]?.prepOptionsOid ?? null;
    try {
      const seq = await prepBatchApi.buildSequence({ testMethodId: effTestMethodId, sort, tier, noRuns: 1, samples: toSort });
      const built = seq.map(seqRowToLocal).map(r => {
        const e = r.sampleLogInOid ? editByOid.get(rowKeyOf(r.sampleLogInOid, r.componentOid, r.testMethodOid)) : undefined;
        const merged = e ? { ...r, ...e } : r;
        const isSample = merged.kind === 'sample';
        return {
          ...merged,
          prepMethod:      merged.prepMethod || commonPrepMethod,
          prepMethodOid:   merged.prepMethodOid ?? commonPrepMethodOid,
          units:           merged.units || commonUnits,
          holdTime:        merged.holdTime || commonHoldTime,
          functionType:    merged.functionType || commonFunctionType,
          functionTypeOid: merged.functionTypeOid ?? commonFunctionTypeOid,
          datePrepared:    merged.datePrepared ?? commonDatePrepared,
          prepOptions:     merged.prepOptions || (isSample ? '' : commonPrepOptions),
          prepOptionsOid:  merged.prepOptionsOid ?? (isSample ? null : commonPrepOptionsOid),
        };
      });
      setRows(renumber(built));
      setSorted(true);
    } catch (e) {
      notify.error(apiErrMsg(e, 'Failed to build the prep sequence.'));
    }
  }

  // Previous ← step back from the built sequence to sample selection (re-pull).
  function goPrevious() { void loadSamples(); }

  // Manually add a QC row under the one checked parent row.
  async function addQc(q: QcTypeOption) {
    if (viewLocked) { notify.warning(SEQ_VIEW_LOCK_MSG); return; }
    const selected = rows.filter(r => r.checked);
    if (selected.length > 1) { notify.warning('Select only one check box.'); return; }
    if (selected.length === 0) { notify.warning('Select a row first, then click ＋ to add QC under it.'); return; }
    const parent = selected[0];
    if (!parent) return;
    try {
      const seq = await prepBatchApi.addQc({
        testMethodId: effTestMethodId, parentSort: parent.sort ?? 0,
        newQcTypeOid: q.qcTypeOid, sequence: rows.map(toWireRow),
      });
      setRows(renumber(seq.map(seqRowToLocal)));
    } catch (e) {
      notify.error(apiErrMsg(e, 'Failed to add QC.'));
    }
  }

  // QC Remove — drop a SINGLE row via its in-grid − button (QC Batch form parity,
  // QcBatchDetailPage.removeRow; the server renumbers via QcSequenceBuilder.RemoveQc).
  // Replaces the old top-toolbar "QC Remove" (checked-rows) button — the remove
  // control now lives inside the grid like the QC form (user request 2026-07-14).
  async function removeRow(id: string) {
    if (viewLocked) { notify.warning(SEQ_VIEW_LOCK_MSG); return; }
    const target = rows.find(r => r.id === id);
    if (!target || target.sort == null) return;
    try {
      const seq = await prepBatchApi.removeQc({ testMethodId: effTestMethodId, sequence: rows.map(toWireRow), removeSorts: [target.sort] });
      setRows(renumber(seq.map(seqRowToLocal)));
    } catch (e) {
      notify.error(apiErrMsg(e, 'Failed to remove the row.'));
    }
  }

  // ── drag & drop reorder (post-Sort) — QC Batch parity (QcBatchDetailPage). The
  // server (QcSequenceBuilder.MoveRow) validates the drop against the row's
  // relationships and returns the resequenced list; a disallowed drop warns and
  // the order is left unchanged. Pre-Sort the order is driven by the checkboxes. ──
  const dragId = useRef<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  async function onDrop(targetId: string) {
    const from = dragId.current;
    setDropTarget(null);
    dragId.current = null;
    if (!from || from === targetId || viewLocked) return;
    const dragRow = rows.find(r => r.id === from);
    const targetRow = rows.find(r => r.id === targetId);
    if (!dragRow || !targetRow || dragRow.sort == null || targetRow.sort == null) return;
    try {
      const seq = await prepBatchApi.moveRow({
        testMethodId: effTestMethodId, dragSort: dragRow.sort, targetSort: targetRow.sort,
        sequence: rows.map(toWireRow),
      });
      setRows(renumber(seq.map(seqRowToLocal)));
    } catch (e) {
      notify.warning(apiErrMsg(e, 'That move is not allowed.'));
    }
  }

  // ── right-click context menu (QC Batch parity — OG SamplePreparationController
  // Grid_FillContextMenuItems :2847 + ContextMenuItemClick :2197): "Copy To All
  // Cell" (always) and "Add Dilution" (post-Sort only, :2865). ──
  const [ctx, setCtx] = useState<{ x: number; y: number; row: PrepRow } | null>(null);
  function closeCtx() { setCtx(null); }
  // Last-focused copyable cell (OG allow-list — SampleAmount / FinalVolume /
  // DatePrepared). Right-clicking a copyable cell selects its column directly.
  const [focusedCol, setFocusedCol] = useState<CopyCol | null>(null);

  // "Copy To All Cell" — copies the right-clicked row's value for the last-focused
  // editable column (SampleAmount / FinalVol / DatePrepared) to every CHECKED row (OG :2203).
  function copyToAll(row: PrepRow) {
    closeCtx();
    if (!focusedCol) return;
    // Filtered view → copy to the VISIBLE checked rows only (never a hidden row).
    const selected = seqView.viewRows.filter(r => r.checked);
    if (selected.length === 0) { notify.warning('Select at least one row to copy to.'); return; }
    const value = row[focusedCol];
    const ids = new Set(selected.map(r => r.id));
    setRows(prev => prev.map(r => (ids.has(r.id) ? { ...r, [focusedCol]: value } : r)));
    prepMsg(`Copied ${COPY_COL_LABEL[focusedCol]} to ${selected.length} selected row${selected.length > 1 ? 's' : ''}.`, 'Copied');
  }

  // "Add Dilution" — mints one "<parentCode>R<n>" replicate under the single
  // CHECKED row via the shared engine (OG :2197). Post-Sort only.
  async function addDilution() {
    closeCtx();
    if (viewLocked) { notify.warning(SEQ_VIEW_LOCK_MSG); return; }
    const selected = rows.filter(r => r.checked);
    if (selected.length > 1) { notify.warning('Select only one check box.'); return; }
    if (selected.length === 0) { notify.warning('Select a row first, then right-click to Add Dilution.'); return; }
    const parent = selected[0];
    if (!parent || parent.sort == null) return;
    if (parent.kind === 'dilution') { notify.warning('Cannot add a dilution to a dilution row.'); return; }
    try {
      const seq = await prepBatchApi.addDilution({
        testMethodId: effTestMethodId, parentSort: parent.sort, sequence: rows.map(toWireRow),
      });
      setRows(renumber(seq.map(seqRowToLocal)));
    } catch (e) {
      notify.error(apiErrMsg(e, 'Failed to add dilution.'));
    }
  }

  // Pre-Sort: tick a sample row to assign it an incremental (1-based) Sort;
  // unticking renumbers the rest so the values stay contiguous.
  function toggleSeqRow(id: string) {
    setRows(prev => {
      const r = prev.find(x => x.id === id);
      if (!r) return prev;
      if (r.checked) {
        const oldSort = r.sort ?? 0;
        return prev.map(x =>
          x.id === id                          ? { ...x, checked: false, sort: 0 }
          : (x.sort != null && x.sort > oldSort) ? { ...x, sort: x.sort - 1 }
          : x);
      }
      const max = prev.reduce((m, x) => x.checked && x.sort != null && x.sort > m ? x.sort : m, 0);
      return prev.map(x => x.id === id ? { ...x, checked: true, sort: max + 1 } : x);
    });
  }
  function toggleAllSeq(on: boolean) {
    // Numbers every row 1..N in RUN order — meaningless on a sorted/filtered view.
    if (viewLocked) return;
    setRows(prev => {
      if (!on) return prev.map(r => ({ ...r, checked: false, sort: 0 }));
      let next = 0;
      return prev.map(r => ({ ...r, checked: true, sort: ++next }));
    });
  }
  // Post-Sort the checkbox is a plain multi-select (for Add QC / QC Remove).
  function toggleSeqRowChecked(id: string) {
    setRows(prev => prev.map(r => (r.id === id ? { ...r, checked: !r.checked } : r)));
  }
  function toggleAllChecked(on: boolean) {
    // Filtered view → select-all acts on the VISIBLE rows only (Excel parity).
    const visible = new Set(seqView.viewRows.map(r => r.id));
    setRows(prev => prev.map(r => (visible.has(r.id) ? { ...r, checked: on } : r)));
  }

  // ── Others tab: Reagents (legacy ReagentLink_Execute parity) ───────────────
  // Loads once a batch is saved; refetched after every link/unlink.
  async function loadReagents(prepOid: string) {
    try {
      setReagentRows(await prepBatchApi.linkedReagents(prepOid));
    } catch {
      notify.error('Could not load linked reagents.');
    }
  }
  useEffect(() => { if (savedPrepId) void loadReagents(savedPrepId); }, [savedPrepId]);

  // Reagent picker OK — reconcile the picker's final staged set against current
  // state. Saved batch: delta link/unlink then refetch; pre-save: replace the
  // local stagedReagents (catalog rows carried through so Create ships them).
  async function reconcileReagents(final: StagedReagent[]) {
    if (!savedPrepId) {
      const prevByKey = new Map(stagedReagents.map(r => [r.reagentID ?? '', r] as const));
      const next: QcAvailableReagent[] = final.map(s =>
        s.catalog ?? prevByKey.get(s.reagentID) ?? {
          reagentID: s.reagentID, reagentName: s.reagentName, prepType: s.prepType, expiryDate: s.expiryDate,
          test: null, datePrepared: null, preparedBy: null, division: null, department: null,
          createdDate: null, dateReceived: null, vendor: null, vendorLT: null,
        });
      setStagedReagents(next);
      const added = final.filter(s => !prevByKey.has(s.reagentID)).length;
      const removed = stagedReagents.filter(r => !final.some(s => s.reagentID === r.reagentID)).length;
      if (added || removed) prepMsg(`${next.length} reagent(s) on the batch.`, 'Reagents');
      return;
    }
    const keptOids = new Set(final.map(s => s.linkedOid).filter((v): v is string => !!v));
    const toRemove = reagentRows.filter(r => !keptOids.has(r.oid));
    const toAdd    = final.filter(s => !s.linkedOid && s.catalog).map(s => s.catalog!);
    if (toRemove.length === 0 && toAdd.length === 0) return;
    try {
      for (const r of toRemove) await prepBatchApi.unlinkReagent(savedPrepId, r.oid, r.reagentID);
      for (const c of toAdd)    await prepBatchApi.linkReagent  (savedPrepId, c);
      await loadReagents(savedPrepId);
      setTabsDirty(true);   // Others-tab edit counts toward the OK dirty-gate (QC parity)
      const parts = [
        toAdd.length    ? `${toAdd.length} added`    : '',
        toRemove.length ? `${toRemove.length} removed` : '',
      ].filter(Boolean).join(', ');
      prepMsg(`Reagents updated — ${parts}.`, 'Reagents');
    } catch (e) {
      notify.error(apiErrMsg(e, 'Updating reagents failed.'));
    }
  }

  async function unlinkSelectedReagents() {
    if (!savedPrepId) {
      if (stagedReagentSel.size === 0) { notify.warning('Select a reagent first.'); return; }
      setStagedReagents(prev => prev.filter(r => !stagedReagentSel.has(r.reagentID ?? '')));
      setStagedReagentSel(new Set());
      return;
    }
    if (reagentSel.size === 0) { notify.warning('Select a reagent first.'); return; }
    try {
      for (const oid of reagentSel) {
        const row = reagentRows.find(r => r.oid === oid);
        await prepBatchApi.unlinkReagent(savedPrepId, oid, row?.reagentID ?? null);
      }
      setReagentSel(new Set());
      await loadReagents(savedPrepId);
      prepMsg('Reagent(s) unlinked.', 'Reagents');
    } catch (e) {
      notify.error(apiErrMsg(e, 'Unlinking reagent failed.'));
    }
  }
  const toggleReagentSel = (oid: string) =>
    setReagentSel(prev => { const n = new Set(prev); if (n.has(oid)) n.delete(oid); else n.add(oid); return n; });
  const toggleStagedReagentSel = (reagentId: string) =>
    setStagedReagentSel(prev => { const n = new Set(prev); if (n.has(reagentId)) n.delete(reagentId); else n.add(reagentId); return n; });
  const toggleAllReagentSel = (on: boolean) =>
    savedPrepId
      ? setReagentSel(on ? new Set(reagentRows.map(r => r.oid)) : new Set())
      : setStagedReagentSel(on ? new Set(stagedReagents.map(r => r.reagentID ?? '')) : new Set());
  const allReagentChecked = savedPrepId
    ? reagentRows.length > 0 && reagentRows.every(r => reagentSel.has(r.oid))
    : stagedReagents.length > 0 && stagedReagents.every(r => stagedReagentSel.has(r.reagentID ?? ''));
  // COA link — reagent-scoped, only when the row's server-computed hasCoa is true.
  // Reuses the QC reagent COA reader (no prep-specific COA endpoint exists).
  const openCoa = async (reagentId: string | null) => {
    if (!reagentId) return;
    try {
      await qcBatchApi.openReagentCoa(reagentId);
    } catch {
      notify.warning('No certificate on file for this reagent.');
    }
  };

  // ── Others tab: Instruments (legacy InstrumentLink_Execute parity) ─────────
  async function loadLinkedInstruments(prepOid: string) {
    try {
      setInstrumentRows(await prepBatchApi.linkedInstruments(prepOid));
    } catch {
      notify.error('Could not load linked instruments.');
    }
  }
  useEffect(() => { if (savedPrepId) void loadLinkedInstruments(savedPrepId); }, [savedPrepId]);

  async function reconcileInstruments(final: QcInstrument[]) {
    if (!savedPrepId) {
      const dedupByOid = new Map<string, QcInstrument>();
      for (const i of final) dedupByOid.set(i.instrumentOid, i);
      const next = Array.from(dedupByOid.values());
      const added = final.filter(i => !stagedInstruments.some(s => s.instrumentOid === i.instrumentOid)).length;
      const removed = stagedInstruments.filter(i => !final.some(s => s.instrumentOid === i.instrumentOid)).length;
      setStagedInstruments(next);
      if (added || removed) prepMsg(`${next.length} instrument(s) on the batch.`, 'Instruments');
      return;
    }
    const preOids = new Set(instrumentRows.map(i => i.instrumentOid));
    const finalOids = new Set(final.map(i => i.instrumentOid));
    const toRemove = instrumentRows.filter(i => !finalOids.has(i.instrumentOid));
    const toAdd    = final.filter(i => !preOids.has(i.instrumentOid));
    if (toRemove.length === 0 && toAdd.length === 0) return;
    try {
      for (const i of toRemove) await prepBatchApi.unlinkInstrument(savedPrepId, i.instrumentOid, i.instrumentName);
      for (const i of toAdd)    await prepBatchApi.linkInstrument  (savedPrepId, i.instrumentOid, i.instrumentName);
      await loadLinkedInstruments(savedPrepId);
      setTabsDirty(true);   // Others-tab edit counts toward the OK dirty-gate (QC parity)
      const parts = [
        toAdd.length    ? `${toAdd.length} added`    : '',
        toRemove.length ? `${toRemove.length} removed` : '',
      ].filter(Boolean).join(', ');
      prepMsg(`Instruments updated — ${parts}.`, 'Instruments');
    } catch (e) {
      notify.error(apiErrMsg(e, 'Updating instruments failed.'));
    }
  }

  async function unlinkSelectedInstruments() {
    if (!savedPrepId) {
      if (stagedInstrumentSel.size === 0) { notify.warning('Select an instrument first.'); return; }
      setStagedInstruments(prev => prev.filter(i => !stagedInstrumentSel.has(i.instrumentOid)));
      setStagedInstrumentSel(new Set());
      return;
    }
    if (instrumentSel.size === 0) { notify.warning('Select an instrument first.'); return; }
    try {
      for (const oid of instrumentSel) {
        const row = instrumentRows.find(r => r.instrumentOid === oid);
        await prepBatchApi.unlinkInstrument(savedPrepId, oid, row?.instrumentName ?? null);
      }
      setInstrumentSel(new Set());
      await loadLinkedInstruments(savedPrepId);
      prepMsg('Instrument(s) unlinked.', 'Instruments');
    } catch (e) {
      notify.error(apiErrMsg(e, 'Unlinking instrument failed.'));
    }
  }
  const toggleInstrumentSel = (oid: string) =>
    setInstrumentSel(prev => { const n = new Set(prev); if (n.has(oid)) n.delete(oid); else n.add(oid); return n; });
  const toggleStagedInstrumentSel = (oid: string) =>
    setStagedInstrumentSel(prev => { const n = new Set(prev); if (n.has(oid)) n.delete(oid); else n.add(oid); return n; });
  const toggleAllInstrumentSel = (on: boolean) =>
    savedPrepId
      ? setInstrumentSel(on ? new Set(instrumentRows.map(i => i.instrumentOid)) : new Set())
      : setStagedInstrumentSel(on ? new Set(stagedInstruments.map(i => i.instrumentOid)) : new Set());
  const allInstrumentChecked = savedPrepId
    ? instrumentRows.length > 0 && instrumentRows.every(i => instrumentSel.has(i.instrumentOid))
    : stagedInstruments.length > 0 && stagedInstruments.every(i => stagedInstrumentSel.has(i.instrumentOid));
  // Others-tab grids — hide / sort / filter / resize (user 2026-09-29, shared othersGrids).
  const othReagentGrid = useOthersReagentGrid({
    saved: !!savedPrepId, linked: reagentRows, staged: stagedReagents,
    onCoa: id => { void openCoa(id); },
    isSelected: k => (savedPrepId ? reagentSel : stagedReagentSel).has(k),
    toggle: k => (savedPrepId ? toggleReagentSel(k) : toggleStagedReagentSel(k)),
    allChecked: allReagentChecked, toggleAll: toggleAllReagentSel,
  });
  const othInstrumentGrid = useOthersInstrumentGrid({
    rows: savedPrepId ? instrumentRows : stagedInstruments,
    isSelected: k => (savedPrepId ? instrumentSel : stagedInstrumentSel).has(k),
    toggle: k => (savedPrepId ? toggleInstrumentSel(k) : toggleStagedInstrumentSel(k)),
    allChecked: allInstrumentChecked, toggleAll: toggleAllInstrumentSel,
  });

  // ── Copy From Previous Batch (prep twin of QcBatchDetailPage.copyFromPrevious) ──
  // OG SamplePreparationController.btnCopyFromSamplePrepAction_Execute (:6125) +
  // CopyingFromPreviousBatch (:6173): checkboxes Reagent · Instrument · Others
  // (=Comment) · All the Above. Reuses the same add paths the pickers use (link on
  // a saved batch, stage otherwise). Sequence / Temperature / Humidity are NOT
  // copied (legacy doesn't either).
  async function copyFromPrevious(sourcePrepOid: string, copyReagents: boolean, copyInstruments: boolean, copyComment: boolean) {
    try {
      let addedR = 0, addedI = 0; let comm = false;
      if (copyComment) {
        // OG "Others" — curBatch.Comments = source.Comments: REPLACE this batch's
        // comment with the source's.
        const detail = await prepBatchApi.getDetail(sourcePrepOid);
        setComment(detail.header.comments ?? '');
        comm = true;
      }
      if (copyReagents) {
        // Copies EXPIRED reagents too, verbatim like OG (SamplePreparationController :6632) —
        // owner 2026-09-26, reversing the 2026-09-18 exclusion (the Add picker offers them too).
        const src = await prepBatchApi.linkedReagents(sourcePrepOid);
        const toAvail = (r: QcLinkedReagent): QcAvailableReagent => ({
          reagentID: r.reagentID, reagentName: r.reagentName, prepType: r.prepType, expiryDate: r.expiredDate,
          test: null, datePrepared: r.datePrepared, preparedBy: r.preparedBy, division: null, department: null,
          createdDate: null, dateReceived: r.dateReceived, vendor: r.vendorOid ?? null, vendorName: r.vendorName, vendorLT: r.vendorLT,
        });
        if (savedPrepId) {
          const have = new Set(reagentRows.map(r => r.reagentID));
          for (const r of src) {
            if (!r.reagentID || have.has(r.reagentID)) continue;
            await prepBatchApi.linkReagent(savedPrepId, toAvail(r)); addedR++;
          }
          if (addedR) await loadReagents(savedPrepId);
        } else {
          const have = new Set(stagedReagents.map(r => r.reagentID));
          const add = src.filter(r => r.reagentID && !have.has(r.reagentID)).map(toAvail);
          addedR = add.length;
          if (add.length) setStagedReagents(prev => [...prev, ...add]);
        }
      }
      if (copyInstruments) {
        // Copy-from-previous excludes RETIRED instruments (user 2026-09-18); `active`
        // is the server bit (RetiredDate IS NULL OR > now). Deliberate divergence from
        // OG (SamplePreparationController :6643 copies them), matching OG's ADD picker.
        const src = (await prepBatchApi.linkedInstruments(sourcePrepOid)).filter(i => i.active !== false);
        if (savedPrepId) {
          const have = new Set(instrumentRows.map(i => i.instrumentOid));
          for (const i of src) {
            if (have.has(i.instrumentOid)) continue;
            await prepBatchApi.linkInstrument(savedPrepId, i.instrumentOid, i.instrumentName); addedI++;
          }
          if (addedI) await loadLinkedInstruments(savedPrepId);
        } else {
          const have = new Set(stagedInstruments.map(i => i.instrumentOid));
          const add = src.filter(i => !have.has(i.instrumentOid));
          addedI = add.length;
          if (add.length) setStagedInstruments(prev => [...prev, ...add]);
        }
      }
      const parts: string[] = [];
      if (copyReagents)    parts.push(`${addedR} reagent(s)`);
      if (copyInstruments) parts.push(`${addedI} instrument(s)`);
      if (comm)            parts.push('the comment');
      prepMsg(`Copied ${parts.join(', ')} from the previous batch.`, 'Copy From Previous');
    } catch (e) {
      notify.error(apiErrMsg(e, 'Copy from previous batch failed.'));
    }
  }

  // OK → persist the batch (mint race-safe PrepBatchID, link samples) via the
  // shared save flow ("Do you want to Save?" → save → "Saved" → back to queue).
  function onOk() {
    if (viewOnly) return;   // read-only (no write permission / completed batch) — never saves
    if (!sorted) { notify.warning('Sort the sequence before saving.'); return; }
    const jobIdsLabel = jobsList.filter(j => jobOids.includes(j.jobOid)).map(j => j.jobID).filter(Boolean).join(';');
    const sequence: PrepSeqRow[] = rows.map(toWireRow);
    // Captured inside `save` so the milestone-success MODAL (§13) can show the
    // minted Prep Batch ID + detail rows after the record is created (user 2026-07-17
    // — the created batch ID must appear in a dialog, not a top toast).
    let minted: PrepSaveResult | null = null;
    void runSave({
      save: () => prepBatchApi.save({
        testMethodId:   effTestMethodId,
        testMethodIds:  effTestMethodIds,
        componentIds:   seed.componentIds,
        matrixId:       seed.matrixId ?? null,
        methodId:       seed.methodId ?? null,
        tier,
        sort,
        jobIds:         jobIdsLabel || null,
        instrumentOid:  instrument,
        instrumentName: instrNames(),
        temperature:    temperature || null,
        humidity:       humidity || null,
        comments:       comment.trim() || null,
        sequence,
        // Others-tab reagents/instruments staged before the batch existed —
        // persisted atomically with Create (mirrors the QC twin).
        stagedReagents: stagedReagents.length ? stagedReagents.map(r => ({
          reagentId: r.reagentID ?? '', reagentName: r.reagentName ?? '', prepType: r.prepType,
          expiredDate: r.expiryDate, vendorLT: r.vendorLT, preparedBy: r.preparedBy,
          datePrepared: r.datePrepared, dateReceived: r.dateReceived, vendorOid: r.vendor ?? null,
        })) : undefined,
        stagedInstrumentOids: stagedInstruments.length ? stagedInstruments.map(i => i.instrumentOid) : undefined,
      }).then(result => { minted = result; setSavedPrepId(result.prepOid); }),
      // §13 milestone-success MODAL (a new prep batch + ID was created) — green,
      // big Prep Batch ID + detail rows, replacing the plain top toast.
      successDialog: () => ({
        type: 'success',
        title: 'Sample Prep Batch Created',
        idLabel: 'Prep Batch ID',
        idValue: minted?.prepBatchId ?? '',
        rows: [
          { label: 'Test', value: effTest || '—' },
          { label: 'Job(s)', value: jobIdsLabel || '—' },
          { label: 'Runs', value: String(minted?.seqCount ?? sequence.length) },
          { label: 'Samples linked', value: String(minted?.samplesLinked ?? 0) },
          // The parameter rows linked across the batch's tests (what "Samples linked" used to show).
          { label: 'Parameters linked', value: String(minted?.parametersLinked ?? 0) },
        ],
        note: 'Returning to the Sample Prep Queue.',
      }),
      onSaved: onBack,
      formName: 'Sample Prep Batches',
      auditAction: 'create',
      onError: (e) => notify.error(apiErrMsg(e, 'Save failed. Please try again.')),
    });
  }

  // Save (edit mode) → A1 reconcile-on-Save: persist the current grid + header
  // Temperature/Humidity/Comments/Instrument back to the saved batch via the shared
  // save flow ("Do you want to Save?" → PUT → "Saved successfully."), then reload
  // from the server (fresh seqOids for added rows, removed rows gone) and stay in
  // edit mode. Twin of QcBatchDetailPage.saveEdits.
  function saveEdits() {
    if (!existingHeader) return;
    // OG SavePrepBatch_Execute on the existing-batch view (SamplePreparationController :6855):
    // an EMPTY sequence grid asks "Removing all samples will delete the PrepID. Do you want to
    // continue?" and, on Yes, DELETES the prep batch (IsDeletePreID → ObjectSpace.Delete :3794)
    // and returns to the list — it never keeps an empty header. Same shared delete flow as the
    // toolbar Delete (samples already returned to the prep queue by the removes).
    if (rows.length === 0) {
      void runDelete({
        formName: 'Sample Prep Batches',
        message: 'Removing all samples will delete the PrepID. Do you want to continue?',
        delete: () => prepBatchApi.deleteBatch(existingHeader.prepBatchOid).then(() => undefined),
        onDeleted: onBack,
        onError: (e) => notify.error(apiErrMsg(e, 'Deleting the prep batch failed.')),
      });
      return;
    }
    const sequence: PrepSeqRow[] = rows.map(toWireRow);
    void runSave({
      save: () => prepBatchApi.updateBatch(existingHeader.prepBatchOid, {
        temperature:    temperature || null,
        humidity:       humidity || null,
        comments:       comment.trim() || null,
        instrumentOid:  instrument,
        instrumentName: instrNames() ?? existingHeader.instrumentName ?? null,
        sequence,
      }).then(() => loadDetail(existingHeader.prepBatchOid)),
      successMessage: 'Saved successfully.',
      successAs: 'dialog',   // shared success MODAL (user 2026-07-17 — consistent with the create dialog)
      onSaved: onBack,
      formName: 'Sample Prep Batches',
      auditAction: 'edit',
      onError: (e) => notify.error(apiErrMsg(e, 'Save failed. Please try again.')),
    });
  }

  // ── toolbar / gating ──
  const canNotes = jobOids.length > 0 || savedPrepId !== null;
  const anySample = rows.some(r => r.kind === 'sample');
  const allChecked = seqView.viewRows.length > 0 && seqView.viewRows.every(r => r.checked);
  const rowClass = (r: PrepRow) => (r.kind === 'sample' ? 'sample' : r.sourceId ? 'child' : 'qc');

  // Export To QC Batch (OG SamplePreparationController.btnExportToQCBatch_Execute).
  // Enabled only on a SAVED batch (OG disables it for a new/unsaved prep batch,
  // ctrl :1657-1664). Validate in OG's message order — status gates first, then
  // the already-in-a-QC-batch guard — then hand the batch to the parent, which
  // swaps to the QC Batch form seeded to auto-import these prepared samples.
  async function runExportToQc() {
    if (!existingHeader || !onExportToQc) return;
    const h = existingHeader;
    // OG btnExportToQCBatch_Execute (:125-130): a multi-test Tier-1 prep batch cannot
    // be exported — a QC batch is single-test. Block it FIRST, matching OG's order.
    if ((h.testMethodId ?? '').split(';').map(s => s.trim()).filter(Boolean).length > 1) {
      void showDialog({ type: 'error', title: 'Export To QC Batch',
        message: 'Cannot export multiple tests in the same QC batch.' });
      return;
    }
    // PrepBatchQueueStatus: 1 Pending Tier2, 2 Pending Result Entry, 3 Result Completed.
    if (h.status === 3) {
      void showDialog({ type: 'error', title: 'Export To QC Batch',
        message: 'Results already entered for this batch.' });
      return;
    }
    if (h.status === 1) {
      void showDialog({ type: 'error', title: 'Export To QC Batch',
        message: 'You need to complete Preparation Tier 2.' });
      return;
    }
    if (h.status !== 2) {
      void showDialog({ type: 'warning', title: 'Export To QC Batch',
        message: 'Complete the sample preparation before exporting to a QC batch.' });
      return;
    }
    try {
      const { hasQcBatch } = await prepBatchApi.exportCheck(h.prepBatchOid);
      if (hasQcBatch) {
        void showDialog({ type: 'error', title: 'Export To QC Batch',
          message: 'A QC Batch ID has already been created for this Job ID.' });
        return;
      }
    } catch (e) {
      notify.error(apiErrMsg(e, 'Failed to check the prep batch before export.'));
      return;
    }
    onExportToQc({
      prepOid:      h.prepBatchOid,
      prepBatchId:  h.prepBatchId ?? '',
      testMethodId: h.testMethodId,
      test:         h.test,
      method:       h.method,
      matrix:       h.matrix,
    });
  }

  const rootCls = ['qcb-root', giCollapsed ? 'geninfo-collapsed' : '', qcCollapsed ? 'qc-collapsed' : '', fullGrid ? 'fullgrid' : '']
    .filter(Boolean).join(' ');

  // ── Phase 2.3 · Keyboard shortcuts (CLAUDE.md standing rule 14, §18.6,
  //    ADR-0013).  Each key MIRRORS a primary Sample Prep Batch action — same
  //    handler, same disabled gate — and is pushed only when that button is
  //    rendered.  Registered UNCONDITIONALLY (this page has no early return
  //    before its render) with a possibly-empty array.
  const hotkeys: HotkeyDef[] = [];
  if (existingHeader && existingHeader.status !== 3) {
    const hdr = existingHeader;   // narrowed non-null for the run closure
    // Delete — mirrors the toolbar Delete (OG status <> ResultCompleted gate).
    hotkeys.push({ combo: 'alt+d', label: 'Delete', group: 'Prep Batch', order: 1,
      run: () => void runDelete({
        formName: 'Sample Prep Batches',
        message: `Delete Prep Batch ${hdr.prepBatchId ?? ''}? Its samples return to the prep queue and this cannot be undone.`,
        delete: () => prepBatchApi.deleteBatch(hdr.prepBatchOid).then(() => undefined),
        onDeleted: onBack,
        onError: (e) => notify.error(apiErrMsg(e, 'Deleting the prep batch failed.')),
      }) });
  }
  if (!existingHeader) {
    // Load (create-flow, primary) — pull the pending prep samples into the grid.
    hotkeys.push({ combo: 'alt+l', label: 'Load', group: 'Prep Batch', order: 2,
      run: () => { void loadSamples(); }, disabled: !loadEnabled || sorted });
  }
  if (!sorted) {
    // Sort — build the prep sequence.
    hotkeys.push({ combo: 'alt+s', label: 'Sort', group: 'Prep Batch', order: 3,
      run: () => { void buildSequence(); }, disabled: !anySample });
  }
  if (sorted && !savedPrepId && !viewOnly) {
    // OK (create) — the commit that saves the new prep batch.
    hotkeys.push({ combo: 'mod+s', label: 'Save', group: 'Prep Batch', order: 4,
      run: () => onOk() });
  }
  if (existingHeader && !viewOnly) {
    // OK (edit-mode save) — reconcile the grid + header back to the saved batch.
    hotkeys.push({ combo: 'mod+s', label: 'Save', group: 'Prep Batch', order: 4,
      run: () => saveEdits(), disabled: !editDirty });
  }
  // ── Toolbar slot buttons (AppToolbar does NOT auto-register `left`/`right` slot
  //    content, so each is bound here explicitly).  Combos mirror each button's
  //    render condition + handler.
  if (existingHeader && !viewOnly) {
    // Add Sample — append pending-prep candidates to a saved, editable batch.
    hotkeys.push({ combo: 'alt+shift+a', label: 'Add Sample', group: 'Prep Batch', order: 5,
      run: () => { void openAddSample(); } });
  }
  // Project Notes — always on the toolbar; gated on a selected job (canNotes).
  hotkeys.push({ combo: 'alt+j', label: 'Project Notes', group: 'Prep Batch', order: 6,
    run: () => setModal('notes'), disabled: !canNotes });
  if (!viewOnly) {
    // Copy From Previous Batch — mutates the current batch (edit-mode only); gated on loaded.
    hotkeys.push({ combo: 'alt+shift+c', label: 'Copy From Previous Batch', group: 'Prep Batch', order: 7,
      run: () => setModal('copy'), disabled: !loaded });
  }
  if (showExportToQc && existingHeader && onExportToQc) {
    // Export To QC Batch — hand this prep batch's samples to a new QC Batch.
    hotkeys.push({ combo: 'alt+shift+x', label: 'Export To QC Batch', group: 'Prep Batch', order: 8,
      run: () => { void runExportToQc(); } });
  }
  if (!existingHeader) {
    // Reset — create-flow only (start a fresh batch).
    hotkeys.push({ combo: 'alt+shift+r', label: 'Reset', group: 'Prep Batch', order: 9,
      run: () => resetBatch() });
  }
  useHotkeys(hotkeys);

  return (
    <>
      <WorkflowStepperBar stepStatus={stepStatus} steps={PREP_STEPS} />
      <div className={rootCls} onClick={() => { setOpenPop(null); closeCtx(); }}>
        <div className="crumb">
          <a onClick={guardedBack}>Sample Prep Queue</a> / <b>Sample Prep Batch</b>
        </div>
        {/* Form-name heading + green guidance/hint strip REMOVED (user UI feedback
            2026-09-16 #2/#3). The form name now shows in the top-bar title (via
            `ai:topbar-title`); Audit moved to the toolbar (below). Back navigation is
            the "Sample Prep Queue" breadcrumb above. */}

        {/* Shared <AppToolbar> — form-specific buttons in the canonical order.
            Reset · Load sit at the far right; OK is the primary commit below.
            NO Refresh here (onRefresh omitted): in the detail view it re-ran the
            pending-samples load, which clears the grid for a SAVED batch (its samples
            are off the pending queue). The create flow uses Reset · Load; QC's detail
            has no Refresh either. (user 2026-07-17) */}
        <div onClick={e => e.stopPropagation()}>
          <AppToolbar
            left={
              <>
                {/* OG parity (SamplePreparationController :559 —
                    DeleteAction.TargetObjectsCriteria = "PrepBatchStatus <> ResultCompleted"):
                    Delete is gated ONLY on status, NOT on view/edit mode, so it shows
                    in VIEW mode too. Hidden only for a ResultCompleted (status 3) batch.
                    QC Remove is NOT a toolbar button; it's the per-row − in the grid.
                    (user 2026-07-17 — enable Delete in view mode.) */}
                {existingHeader && existingHeader.status !== 3 && (
                  <button className="ca-btn" title={`Delete (${formatCombo('alt+d')})`}
                    onClick={() => void runDelete({
                      formName: 'Sample Prep Batches',
                      message: `Delete Prep Batch ${existingHeader.prepBatchId ?? ''}? Its samples return to the prep queue and this cannot be undone.`,
                      delete: () => prepBatchApi.deleteBatch(existingHeader.prepBatchOid).then(() => undefined),
                      onDeleted: onBack,
                      onError: (e) => notify.error(apiErrMsg(e, 'Deleting the prep batch failed.')),
                    })}><i className="ti ti-trash" /> Delete</button>
                )}
                {/* Add Sample (OG AddSample_Execute) — appends more pending-prep
                    candidates to a SAVED batch's sequence. OG activates it ONLY on the
                    existing-batch edit view (SamplePrepBatch_DetailView_Copy_History,
                    ViewEditMode==Edit && SamplepreparationWrite — SamplePreparationController
                    :2530-2542), NEVER on the new-batch build view. So it shows only for an
                    editable EXISTING batch, not during the Job→Load→Sort→OK create flow.
                    (fix 2026-07-16 — was `!viewOnly && loaded`, which wrongly showed it
                    after Load on a new batch once existing-batch editing landed.) */}
                {existingHeader && !viewOnly && (
                  <button className="ca-btn" title={`Add Sample (${formatCombo('alt+shift+a')})`}
                    onClick={() => void openAddSample()}><i className="ti ti-plus" /> Add Sample</button>
                )}
                {/* Project Notes stays available in VIEW mode (viewing notes is a
                    read action). Copy From Previous MUTATES the current batch, so it
                    is EDIT-mode only (hidden in view), like Add Sample / Delete. */}
                <button className="ca-btn" disabled={!canNotes} title={(canNotes ? 'Project Notes' : 'Select a job first') + ` (${formatCombo('alt+j')})`}
                  onClick={() => setModal('notes')}><i className="ti ti-notes" /> Project Notes{notesCount != null ? ` (${notesCount})` : ''}</button>
                {!viewOnly && (
                  <button className="ca-btn" disabled={!loaded} title={`Copy From Previous Batch (${formatCombo('alt+shift+c')})`}
                    onClick={() => setModal('copy')}><i className="ti ti-copy" /> Copy From Previous Batch</button>
                )}
                {showExportToQc && existingHeader && onExportToQc && (
                  <button className="ca-btn"
                    title={`Export this prep batch's samples to a new QC Batch (${formatCombo('alt+shift+x')})`}
                    onClick={() => void runExportToQc()}><i className="ti ti-table-export" /> Export To QC Batch</button>
                )}
              </>
            }
            right={
              <>
                {/* Audit Trail — moved here (before Reset) from the removed form-title
                    header (user UI feedback 2026-09-16 #4). Config-gated chip self-hides
                    when audit is off. marginLeft:auto right-aligns the Audit·Reset·Load group. */}
                <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center' }}>
                  <AuditTrailButton variant="chip" formName="Sample Prep Batches" sourceId={savedPrepId ?? undefined} />
                </span>
                {!existingHeader && (
                  <button className="ca-btn" onClick={resetBatch} title={`Reset (${formatCombo('alt+shift+r')})`}>
                    <i className="ti ti-rotate" /> Reset</button>
                )}
                {!existingHeader && (
                  <button className="ca-btn ca-btn-solid" disabled={!loadEnabled || sorted} onClick={() => loadSamples()}
                    title={`Load pending samples (${formatCombo('alt+l')})`}>
                    <span aria-hidden="true">⬇️</span>&nbsp;Load</button>
                )}
              </>
            }
          />
        </div>

        {/* detail card */}
        <div className="card detailcard" onClick={e => { e.stopPropagation(); setOpenPop(null); closeCtx(); }}>
          <div className="cardhd">
            <div className="ttl"><span className="bar" /> General Information</div>
            <div className="sp" />
            <button className="iconbtn" title="Collapse / expand details" onClick={() => setGiCollapsed(v => !v)}>{giCollapsed ? '▾' : '▴'}</button>
            <button className="iconbtn" title="Hide / show QC Type panel" onClick={() => setQcCollapsed(v => !v)}>◧</button>
            <button className="iconbtn" title="Full screen grid" onClick={() => setFullGrid(v => !v)}>{fullGrid ? '⤡' : '⛶'}</button>
          </div>

          <div className="geninfo">
            <div className="form-grid">
              <Fld label="Prep Batch ID:"><input value={existingHeader?.prepBatchId ?? 'Autogenerate'} readOnly /></Fld>
              {/* A multi-test batch lists every test ("A; B"); hover shows the full list when it is cut off. */}
              <Fld label="Test:" req><input value={effTest ?? ''} title={effTest ?? ''} readOnly /></Fld>
              <Fld label="Tier:"><input value={String(tier)} readOnly /></Fld>

              <Fld label="Date Created:"><input
                value={fmtLocalDate(existingHeader?.createdDate ?? new Date().toISOString())} readOnly /></Fld>
              <Fld label="Method:" req><input value={effMethod ?? ''} readOnly /></Fld>
              <Fld label="Humidity(%):" yellow><input value={humidity} disabled={headerLocked} onChange={e => setHumidity(e.target.value)} /></Fld>

              <Fld label="Created By:" yellow><input value={existingHeader?.createdByName || meName || 'Service'} readOnly /></Fld>
              <PickerFld label="Job ID:" req open={openPop === 'job'} disabled={sorted || viewOnly}
                onToggle={() => setOpenPop(openPop === 'job' ? null : 'job')}
                text={jobLabel} placeholder={!jobOids.length}>
                {openPop === 'job' && (
                  <div className="pop">
                    <div className="popscroll"><table>
                      <thead><tr><th className="cbcol">#</th><th>Job ID</th><th className="num">Sx</th><th>TAT</th><th>Due Date</th><th>Date Received</th></tr></thead>
                      <tbody>
                        {jobsList.length === 0 && <tr><td colSpan={6} className="nodata">No jobs with pending samples.</td></tr>}
                        {jobsList.map(j => (
                          <tr key={j.jobOid} className={jobOids.includes(j.jobOid) ? 'on' : ''} onClick={() => toggleJob(j.jobOid)}>
                            <td className="cbcol"><input type="checkbox" className="chk" readOnly checked={jobOids.includes(j.jobOid)} /></td>
                            <td>{j.jobID}</td>
                            <td className="num">{j.sampleCount}</td>
                            <td>{j.tat ?? ''}</td>
                            <td>{fmtDate(j.dueDate)}</td>
                            <td>{fmtDate(j.receivedDate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table></div>
                    <div className="pfoot"><span>Page 1 of 1 ({jobsList.length} items)</span>
                      <button className="apply" onClick={() => setOpenPop(null)}>Apply</button></div>
                  </div>
                )}
              </PickerFld>
              <Fld label="Temp(°C):" yellow><input value={temperature} disabled={headerLocked} onChange={e => setTemperature(e.target.value)} /></Fld>

              <Fld label="Matrix:" req><input value={effMatrix ?? ''} readOnly /></Fld>
              <PickerFld label="Instrument:" req open={openPop === 'instr'} disabled={sorted || viewOnly}
                onToggle={() => setOpenPop(openPop === 'instr' ? null : 'instr')}
                text={instrLabel} placeholder={!instrument}>
                {openPop === 'instr' && (
                  <div className="pop">
                    <div className="popscroll"><table>
                      <thead><tr><th className="cbcol">#</th><th>InstrumentNumber</th><th>InstrumentName</th></tr></thead>
                      <tbody>
                        {instrList.length === 0 && <tr><td colSpan={3} className="nodata">No instruments linked to this test.</td></tr>}
                        {instrList.map(ins => (
                          <tr key={ins.instrumentOid} className={instrOids.includes(ins.instrumentOid) ? 'on' : ''} onClick={() => pickInstr(ins.instrumentOid)}>
                            <td className="cbcol"><input type="checkbox" className="chk" readOnly checked={instrOids.includes(ins.instrumentOid)} /></td>
                            <td>{ins.labwareName}</td>
                            <td>{ins.instrumentName}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table></div>
                    <div className="pfoot"><span>Page 1 of 1 ({instrList.length} items)</span>
                      <button className="apply" onClick={() => setOpenPop(null)}>Apply</button></div>
                  </div>
                )}
              </PickerFld>
            </div>
          </div>

          {/* tabs */}
          <div className="tabsrow">
            <div className="tabs">
              <div className={`tab ${tab === 'seq' ? 'active' : ''}`} onClick={() => setTab('seq')}>Batch Sequence</div>
              <div className={`tab ${tab === 'others' ? 'active' : ''}`} onClick={() => setTab('others')}>Others</div>
            </div>
            <div className="gridctl">
              <button className="btn" onClick={() => setGiCollapsed(v => !v)}>{giCollapsed ? '▾ Show details' : '▴ Collapse details'}</button>
              <button className="btn" onClick={() => setFullGrid(v => !v)}>{fullGrid ? '⤡ Exit full screen' : '⛶ Full screen'}</button>
            </div>
          </div>

          {tab === 'seq' ? (
            <div className="bseq">
              <div className="qcpanel">
                <div className="qh"><span>QCType</span><span>▲</span></div>
                <div className="qlist">
                  {loaded ? qcTypeList.filter(q => q.panelVisible).map(q => (
                    <div className="qitem" key={q.qcTypeOid}>
                      <span>{q.qcTypeName}</span>
                      {sorted && !viewOnly && <button className="circ add"
                        title={viewLocked ? SEQ_VIEW_LOCK_MSG : `Add ${q.qcTypeName}`}
                        disabled={viewLocked}
                        onClick={() => void addQc(q)}>＋</button>}
                    </div>
                  )) : (
                    <div className="nodata" style={{ padding: 18 }}>Load samples to see QC types.</div>
                  )}
                </div>
                <div className="qcount">Count={loaded ? qcTypeList.filter(q => q.panelVisible).length : 0}</div>
              </div>
              <div className={`seqwrap ${sorted ? '' : 'presort'} ${viewOnly ? 'viewonly' : ''} ${viewLocked ? 'viewlocked' : ''}`}
                   style={frozenWidthVars(k => !fzShown(k))}>
                {/* Grid toolbar strip — OG has the XAF default "Export to" dropdown
                    here, directly above the sequence grid's column headers. Export is
                    always the FULL run order, even while the view is sorted/filtered. */}
                <div className="seqgridbar">
                  <ExportMenu filename={`prep-batch-sequence${existingHeader?.prepBatchId ? `-${existingHeader.prepBatchId}` : ''}`}
                              headers={seqExport.headers} rows={seqExport.rows} />
                  <SeqViewNote view={seqView} total={rows.length} />
                </div>
                <div className="stscroll">
                  <table>
                    {/* Pin the 7 frozen leading columns to exact widths via <col>
                        (auto layout honours a column-level width even for the
                        narrow sticky Sort cell, where a cell min-width was ignored
                        and collapsed on scroll). The data columns get NO <col> →
                        content-sized, exactly like the QC form. (user 2026-07-14) */}
                    <colgroup>
                      <col className="c-drag" />
                      <col className="c-cb" />
                      <col className="c-rm" />
                      {fzShown('qc') && <col className="c-qct" />}
                      {fzShown('sort') && <col className="c-sort" />}
                      {fzShown('code') && <col className="c-sys" />}
                      {fzShown('job') && <col className="c-job" />}
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="cbcol frz fz0" />
                        <th className="cbcol frz fz1"><input type="checkbox" className="chk" checked={allChecked}
                          disabled={viewOnly || (!sorted && viewLocked)}
                          title={!sorted && viewLocked ? SEQ_VIEW_LOCK_MSG : undefined}
                          onChange={e => (sorted ? toggleAllChecked(e.target.checked) : toggleAllSeq(e.target.checked))} /></th>
                        <th className="cbcol frz fz2" />
                        {/* Frozen identity columns — click-to-sort + filter chevron (view-only)
                            and the header right-click menu (Sort · Hide · Show All · Chooser);
                            a hidden one is not rendered and its width var collapses to 0.
                            The block matches the QC Batch form EXACTLY (shared qcBatch.css
                            offsets): SysSampleCode = fz5 (--w-sys) then JobID = fz6
                            (--w-job). DF is NON-frozen and sits AFTER the frozen block
                            (col-df), like QC — a non-frozen column can never be wedged
                            between two frozen ones or the sticky chain breaks and the Sort
                            column collapses on horizontal scroll (user 2026-07-14). */}
                        {([['qc', 'frz fz3', 'QCType'], ['sort', 'num frz fz4', 'Sort'],
                           ['code', 'frz fz5', 'SysSamplecode'], ['job', 'frz fz6', 'JobID']] as const)
                          .filter(([k]) => fzShown(k)).map(([k, cls, label]) => (
                            <th key={k} className={`${cls}${fzEdge(k)}`} onClick={() => seqView.onSort(k)}
                                onContextMenu={(e) => seqMenu.openHeaderMenu(e, { field: k, label })}>
                              {seqView.header(k, label)}
                            </th>
                          ))}
                        {/* Pre-Sort shows the DilutionCount (its full caption needs the
                            wider col-num-sm); post-Sort it's the short "DF" (col-df).
                            Matches the QC Batch grid. (user 2026-07-14) */}
                        {/* Non-frozen DATA columns — array-driven from the column model
                            so Hide / Column Chooser drop them from BOTH header and body.
                            Each carries the shared .adg-resize handle (position:relative
                            anchors it). DupSource/SpikeSource keep .col-dupspike so they
                            still collapse pre-Sort via CSS. */}
                        {seqDataCols.map(c => (
                          <th key={c.key}
                              className={seqHClass[c.key] ?? ''}
                              style={{ position: 'relative', ...seqW(c.key) }}
                              onClick={() => seqView.onSort(c.key)}
                              onContextMenu={(e) => seqMenu.openHeaderMenu(e, { field: c.key, label: c.label })}>
                            {seqView.header(c.key, c.label)}
                            <span className="adg-resize" onMouseDown={(e) => seqCm.startResize(c.key, e)} onClick={(e) => e.stopPropagation()} />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr><td colSpan={3 + seqCm.visibleColumns.length} className="nodata">No data to display — pick Job ID + Instrument and click <b>Load</b>, then <b>Sort</b>.</td></tr>
                      ) : seqView.viewRows.length === 0 ? (
                        <tr><td colSpan={3 + seqCm.visibleColumns.length} className="nodata">No rows match the column filter.</td></tr>
                      ) : seqView.viewRows.map(r => (
                        <tr key={r.id}
                          className={`${rowClass(r)} ${r.checked ? 'sel' : ''} ${dropTarget === r.id ? 'dropbefore' : ''}`}
                          draggable={sorted && !viewOnly && !viewLocked}
                          onDragStart={() => { if (sorted && !viewOnly && !viewLocked) dragId.current = r.id; }}
                          onDragOver={e => { if (!sorted || viewOnly || viewLocked) return; e.preventDefault(); if (r.id !== dragId.current) setDropTarget(r.id); }}
                          onDrop={e => { if (!sorted || viewOnly || viewLocked) return; e.preventDefault(); void onDrop(r.id); }}
                          onDragEnd={() => setDropTarget(null)}
                          onClick={() => { if (sorted && !viewOnly) toggleSeqRowChecked(r.id); }}
                          onContextMenu={e => {
                            if (viewOnly) return;   // nothing mutating to offer in view mode
                            e.preventDefault();
                            // Right-click ON a copyable cell picks that column for Copy To All Cell.
                            const col = (e.target as HTMLElement).closest<HTMLElement>('[data-copycol]')?.dataset.copycol;
                            if (col) setFocusedCol(col as CopyCol);
                            setCtx({ x: Math.min(e.clientX, window.innerWidth - 190), y: Math.min(e.clientY, window.innerHeight - 120), row: r });
                          }}>
                          {/* Drag-to-reorder handle appears only AFTER Sort — pre-Sort the
                              run order is driven by the checkboxes, not dragging (QC parity). */}
                          <td className="drag frz fz0" title={sorted && !viewOnly ? (viewLocked ? SEQ_VIEW_LOCK_MSG : 'Drag to reorder') : ''}>{sorted && !viewOnly ? '⠿' : ''}</td>
                          <td className="cbcol frz fz1"><input type="checkbox" className="chk"
                            checked={r.checked}
                            disabled={viewOnly}
                            onClick={e => e.stopPropagation()}
                            onChange={() => { if (sorted) toggleSeqRowChecked(r.id); else toggleSeqRow(r.id); }} /></td>
                          {/* Per-row QC Remove − (QC Batch form parity) — appears only
                              AFTER Sort, hidden in view mode. Replaces the old top-toolbar
                              "QC Remove" button (user 2026-07-14). */}
                          <td className="cbcol frz fz2">{sorted && !viewOnly && <button className="circ rm"
                            title={viewLocked ? SEQ_VIEW_LOCK_MSG : 'Remove'} disabled={viewLocked}
                            onClick={e => { e.stopPropagation(); void removeRow(r.id); }}>−</button>}</td>
                          {fzShown('qc') && <td className={`qct frz fz3${fzEdge('qc')}`}>{r.qc}</td>}
                          {fzShown('sort') && <td className={`num frz fz4${fzEdge('sort')}`}>{r.sort ?? ''}</td>}
                          {fzShown('code') && <td className={`frz fz5${fzEdge('code')}`}>{r.code || ''}</td>}
                          {/* JobID shows only for a Sample-QCType row (OG parity) — QC
                              standard rows (CCB/ICV/LCS/…) leave it blank. */}
                          {fzShown('job') && <td className={`frz fz6${fzEdge('job')}`}>{r.qc === 'Sample' ? (r.job || '') : ''}</td>}
                          {/* Pre-Sort EDITABLE prep cells — sample rows only; QC rows
                              stay blank/read-only. Post-Sort/viewOnly = read-only
                              display (unchanged). OG SamplePreparationController load
                              grid makes DilutionCount/SampleAmount/FinalVolume/
                              PrepNameOptions(combo)/DatePrepared editable. */}
                          {/* Pre-Sort: editable DilutionCount (sample rows). Post-Sort: editable
                              DF (all rows — QC parity, OG AllowEdit). Read-only in view mode. */}
                          {(() => {
                            // Body cells for the non-frozen DATA columns, keyed by id —
                            // same set + order as the header (visibleColumns) so a Hide
                            // drops the cell from both. seqW() applies a dragged width over
                            // the fixed CSS min/max. All the viewOnly/sorted/kind editable
                            // branches are unchanged, just re-homed into the map.
                            const cell: Record<string, ReactNode> = {
                              dilution: viewOnly ? (
                                <td key="dilution" className={`num ${sorted ? 'col-df' : 'col-num-sm'}`} style={seqW('dilution')}>{sorted ? (r.df || '') : (r.dilutionCount ?? '')}</td>
                              ) : sorted ? (
                                <td key="dilution" className="num col-df edcell" style={seqW('dilution')}><input className="cellinp num" type="number" min={1}
                                  value={r.df || ''}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => updateRow(r.id, { df: e.target.value === '' ? 0 : Math.max(0, Number(e.target.value) || 0) })}
                                  onBlur={e => updateRow(r.id, { df: Math.max(1, Number(e.target.value) || 1) })} /></td>
                              ) : r.kind === 'sample' ? (
                                <td key="dilution" className="num col-num-sm edcell" style={seqW('dilution')}>
                                  <DilutionCell value={r.dilutionCount ?? 1}
                                    onCommit={n => updateRow(r.id, { dilutionCount: n })} />
                                </td>
                              ) : (
                                <td key="dilution" className="num col-num-sm" style={seqW('dilution')}>{r.dilutionCount ?? ''}</td>
                              ),
                              // SampleAmount / FinalVol — editable pre- AND post-Sort (all rows); read-only in view mode.
                              sampleAmount: !viewOnly ? (
                                <td key="sampleAmount" data-copycol="sampleAmount" className="num col-num-sm edcell" style={seqW('sampleAmount')}><input className="cellinp num"
                                  value={r.sampleAmount}
                                  onChange={e => updateRow(r.id, { sampleAmount: e.target.value })}
                                  onFocus={() => setFocusedCol('sampleAmount')}
                                  onClick={e => e.stopPropagation()} /></td>
                              ) : (
                                <td key="sampleAmount" className="num col-num-sm" style={seqW('sampleAmount')}>{r.sampleAmount || ''}</td>
                              ),
                              finalVol: !viewOnly ? (
                                <td key="finalVol" data-copycol="finalVol" className="num edcell" style={seqW('finalVol')}><input className="cellinp num"
                                  value={r.finalVol}
                                  onChange={e => updateRow(r.id, { finalVol: e.target.value })}
                                  onFocus={() => setFocusedCol('finalVol')}
                                  onClick={e => e.stopPropagation()} /></td>
                              ) : (
                                <td key="finalVol" className="num" style={seqW('finalVol')}>{r.finalVol || ''}</td>
                              ),
                              // Dup/Spike SOURCE — selection combo on rows with valid
                              // candidates, plain text otherwise (twin of the QC form).
                              // Dropdown ONLY when there is a real choice (2+ candidates);
                              // one candidate renders as text, and no blank entry is
                              // offered. Twin of the QC form (user 2026-08-07).
                              dupSource: (
                                <td key="dupSource" className="col-dupspike edcell" style={seqW('dupSource')}>
                                  {sorted && !viewOnly && (sourceOpts.get(r.id)?.dup.length ?? 0) > 1
                                    ? <select className="cellinp" value={r.dupSource || ''}
                                        onClick={e => e.stopPropagation()}
                                        onChange={e => updateRow(r.id, { dupSource: e.target.value })}>
                                        {sourceOpts.get(r.id)!.dup.map(o => <option key={o} value={o}>{o}</option>)}
                                      </select>
                                    : (sorted ? (r.dupSource || '') : '')}
                                </td>
                              ),
                              spikeSource: (
                                <td key="spikeSource" className="col-dupspike edcell" style={seqW('spikeSource')}>
                                  {sorted && !viewOnly && (sourceOpts.get(r.id)?.spike.length ?? 0) > 1
                                    ? <select className="cellinp" value={r.spikeSource || ''}
                                        onClick={e => e.stopPropagation()}
                                        onChange={e => updateRow(r.id, { spikeSource: e.target.value })}>
                                        {sourceOpts.get(r.id)!.spike.map(o => <option key={o} value={o}>{o}</option>)}
                                      </select>
                                    : (sorted ? (r.spikeSource || '') : '')}
                                </td>
                              ),
                              test:           <td key="test" style={seqW('test')}>{r.test || ''}</td>,
                              method:         <td key="method" style={seqW('method')}>{r.method || ''}</td>,
                              component:      <td key="component" style={seqW('component')}>{r.component || ''}</td>,
                              sampleName:     <td key="sampleName" style={seqW('sampleName')}>{r.sampleName || ''}</td>,
                              siteName:       <td key="siteName" style={seqW('siteName')}>{r.siteName || ''}</td>,
                              samplingAddress:<td key="samplingAddress" style={seqW('samplingAddress')}>{r.samplingAddress || ''}</td>,
                              units:          <td key="units" style={seqW('units')}>{r.units || ''}</td>,
                              prepMethod:     <td key="prepMethod" style={seqW('prepMethod')}>{r.prepMethod || ''}</td>,
                              // PrepOptions — combo over the Settings ▸ Utility ▸ Prep Options master
                              // (dbo.PrepOptions), editable on every row pre- AND post-Sort, read-only in
                              // view mode. Persists SamplePrepBatchSequence.PrepNameOptions (the Oid) on
                              // Create / Update. Restored 2026-09-28 (removed 2026-08-14 by NTU/SamplePrep).
                              prepOptions: !viewOnly ? (
                                <td key="prepOptions" className="edcell" style={seqW('prepOptions')}><select className="cellinp" value={r.prepOptionsOid ?? ''}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => {
                                    const o = prepOptionsList.find(x => x.oid === e.target.value);
                                    updateRow(r.id, { prepOptionsOid: o?.oid ?? null, prepOptions: o?.prepName ?? '' });
                                  }}>
                                  <option value=""></option>
                                  {/* A saved value no longer in the list (option retired) stays selectable/visible. */}
                                  {r.prepOptionsOid && !prepOptionsList.some(o => o.oid === r.prepOptionsOid) &&
                                    <option value={r.prepOptionsOid}>{r.prepOptions}</option>}
                                  {prepOptionsList.map(o => <option key={o.oid} value={o.oid}>{o.prepName}</option>)}
                                </select></td>
                              ) : (
                                <td key="prepOptions" style={seqW('prepOptions')}>{r.prepOptions || ''}</td>
                              ),
                              // NTU (turbidity) — editable numeric on every row, pre/post-Sort; read-only in
                              // view mode. Drives the computed SamplePrep column. (feature 2026-08-14)
                              ntu: !viewOnly ? (
                                <td key="ntu" className="num col-num-sm edcell" style={seqW('ntu')}><input className="cellinp num" type="number" step="any" min={0}
                                  value={r.ntu}
                                  onChange={e => updateRow(r.id, { ntu: e.target.value })}
                                  onClick={e => e.stopPropagation()} /></td>
                              ) : (
                                <td key="ntu" className="num col-num-sm" style={seqW('ntu')}>{r.ntu || ''}</td>
                              ),
                              // SamplePrep — READ-ONLY, computed from NTU by samplePrepFromNtu (NTU >= 1.0 → Yes,
                              // else No); blank until an NTU value is entered. (feature 2026-08-14)
                              samplePrep: (() => {
                                const sp = samplePrepFromNtu(r.ntu);
                                // Yes = red, No = green (user request 2026-08-14); blank stays uncoloured.
                                const spColor = sp === 'Yes' ? 'var(--ai-red)' : sp === 'No' ? 'var(--ai-green)' : undefined;
                                return <td key="samplePrep" style={{ ...seqW('samplePrep'), color: spColor, fontWeight: sp ? 600 : undefined }}>{sp}</td>;
                              })(),
                              // FunctionType (SamplePrepType) — READ-ONLY in both states.
                              functionType: <td key="functionType" style={seqW('functionType')}>{r.functionType || ''}</td>,
                              holdTime:     <td key="holdTime" style={seqW('holdTime')}>{r.holdTime || ''}</td>,
                              // DatePrepared — shared InlineCellEditor (datetime); editable on EVERY row
                              // (samples, QC, dilutions) pre- and post-Sort as in G5, read-only in view mode.
                              // A copyable column for Copy To All Cell.
                              datePrepared: (
                                <td key="datePrepared" data-copycol={viewOnly ? undefined : 'datePrepared'} style={seqW('datePrepared')}
                                  onMouseDown={() => { if (!viewOnly) setFocusedCol('datePrepared'); }}
                                  onClick={e => e.stopPropagation()}>
                                  <InlineCellEditor value={r.datePrepared} type="datetime"
                                    disabled={viewOnly}
                                    onCommit={wire => updateRow(r.id, { datePrepared: wire })} />
                                </td>
                              ),
                              dateReceived:  <td key="dateReceived" style={seqW('dateReceived')}>{fmtLocalShort(r.dateReceived)}</td>,
                              dateCollected: <td key="dateCollected" style={seqW('dateCollected')}>{fmtLocalShort(r.dateCollected)}</td>,
                              client:        <td key="client" style={seqW('client')}>{r.client || ''}</td>,
                              projectId:     <td key="projectId" style={seqW('projectId')}>{r.projectId || ''}</td>,
                            };
                            return seqDataCols.map(c => cell[c.key]);
                          })()}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Header right-click menu (Hide · Show All · Column Chooser) +
                      the Column Chooser popup — both portal to <body>, rendered once. */}
                  {seqMenu.headerMenu}
                  {seqCm.chooser}
                </div>
                <div className="qcount">Count={viewLocked ? `${seqView.viewRows.length} of ${rows.length}` : rows.length}</div>
              </div>
            </div>
          ) : (
            <div className="otherwrap">
              {/* Reagent / Instrument linking — legacy ReagentLink/InstrumentLink
                  parity, ported from the QC twin.  Pre-save picks stage locally and
                  ship with Create; a saved batch links/unlinks immediately.  The
                  Comment editor persists with the batch on OK. */}
              <OtherSection title="Reagent"
                onAdd={() => setModal('reagent')}
                onRemove={unlinkSelectedReagents}
                disabled={viewOnly}
                count={othReagentGrid.viewRows.length}
                grid={othReagentGrid.table} />
              <OtherSection title="Instrument"
                onAdd={() => setModal('instr2')}
                onRemove={unlinkSelectedInstruments}
                disabled={viewOnly}
                count={othInstrumentGrid.viewRows.length}
                grid={othInstrumentGrid.table} />
              <div className="osect">
                <h3>Comment</h3>
                <div className="commentbox">
                  <textarea className="editor" value={comment} disabled={viewOnly}
                    placeholder={viewOnly ? '' : 'Add a comment for this batch…'}
                    onChange={e => setComment(e.target.value)} />
                </div>
              </div>
            </div>
          )}

          <div className="detail-actions">
            {!existingHeader && <button className="btn" disabled={!sorted} onClick={goPrevious}>‹ Previous</button>}
            {!sorted && <button className={`btn ${anySample ? 'primary' : ''}`} disabled={!anySample} onClick={buildSequence}
              title={`Sort — build the prep sequence (${formatCombo('alt+s')})`}>Sort ⇅</button>}
            {/* OK is the CREATE action — hidden once the batch has an Oid (a
                just-created or an opened existing batch). */}
            {sorted && !savedPrepId && !viewOnly && <button className="btn primary" onClick={onOk}
              title={`Save this batch (${formatCombo('mod+s')})`}>OK</button>}
            {/* OK is the EDIT-mode persist action (A1) — shown when an existing,
                still-editable (not ResultCompleted) batch is open in edit mode. */}
            {existingHeader && !viewOnly &&
              <button className="btn primary" onClick={saveEdits} disabled={!editDirty}
                title={`${editDirty ? 'Save changes' : 'No changes to save'} (${formatCombo('mod+s')})`}>OK</button>}
          </div>
        </div>

        {modal === 'addsample' && <AddSamplePicker
          candidates={addSampleCandidates}
          onClose={() => setModal(null)}
          onCommit={commitAddSample} />}
        {modal === 'notes' && jobOids[0] &&
          <NotesModal jobOid={jobOids[0]} jobLabel={jobLabel} onClose={() => { setModal(null); void loadNotesCount(); }} />}
        {/* Others-tab pickers open PRE-SEEDED with whatever's already linked/staged
            (RIGHT panel); OK returns the final set and reconcile* applies the delta. */}
        {modal === 'instr2' && <InstrPicker
          testMethodId={effTestMethodId || null}
          preLinked={savedPrepId ? instrumentRows : stagedInstruments}
          onClose={() => setModal(null)}
          onCommit={fin => { setModal(null); void reconcileInstruments(fin); }} />}
        {modal === 'reagent' && <ReagentPicker
          testMethodId={effTestMethodId || null}
          preLinked={
            savedPrepId
              ? reagentRows.map(r => ({ oid: r.oid, reagentID: r.reagentID, reagentName: r.reagentName, prepType: r.prepType, expiryDate: r.expiredDate, test: null, datePrepared: r.datePrepared, preparedBy: r.preparedBy, dateReceived: r.dateReceived }))
              : stagedReagents.map(r => ({ reagentID: r.reagentID, reagentName: r.reagentName, prepType: r.prepType, expiryDate: r.expiryDate, test: r.test, datePrepared: r.datePrepared, preparedBy: r.preparedBy, dateReceived: r.dateReceived }))
          }
          onClose={() => setModal(null)}
          onCommit={fin => { setModal(null); void reconcileReagents(fin); }} />}
        {modal === 'copy' && <CopyModal
          testMethodId={effTestMethodId}
          excludePrepOid={savedPrepId ?? undefined}
          onClose={() => setModal(null)}
          onApply={(src, reag, instr, comm) => { setModal(null); void copyFromPrevious(src, reag, instr, comm); }} />}
      </div>

      {/* right-click context menu — only rendered when it has an applicable item:
          Copy To All Cell (a copyable cell was focused) or Add Dilution (post-Sort).
          QC Batch parity — same behaviour as QcBatchDetailPage. */}
      {ctx && (focusedCol || sorted) && (
        <div className="qcb-ctxmenu" style={{ left: ctx.x, top: ctx.y }} onClick={e => e.stopPropagation()}>
          {focusedCol && <div onClick={() => copyToAll(ctx.row)}>▦ Copy To All Cell</div>}
          {sorted && <div onClick={() => void addDilution()}>💧 Add Dilution</div>}
        </div>
      )}
    </>
  );
}

// ── small field wrappers (copied from the QC twin) ───────────────────────────
function Fld({ label, req, yellow, children }: { label: string; req?: boolean; yellow?: boolean; children: ReactNode }) {
  return (
    <div className="fld">
      <label>{label}{req && <span className="star">*</span>}</label>
      <div className={`ctl ${yellow ? 'yellow' : ''}`}>{children}</div>
    </div>
  );
}
function PickerFld({ label, req, open, onToggle, text, placeholder, disabled, children }:
  { label: string; req?: boolean; open: boolean; onToggle: () => void; text: string; placeholder: boolean; disabled?: boolean; children: ReactNode }) {
  return (
    <div className="fld pos" onClick={e => e.stopPropagation()}>
      <label>{label}{req && <span className="star">*</span>}</label>
      <div className="ctl">
        <div className={`picker ${req && placeholder ? 'req' : ''} ${disabled ? 'disabled' : ''}`}
          onClick={disabled ? undefined : onToggle}>
          <span className={placeholder ? 'ph' : ''}>{text}</span><span className="car">▾</span>
        </div>
        {open && !disabled && children}
      </div>
    </div>
  );
}

// Short date for picker cells via the shared helper (§18.6 — never inline).
function fmtDate(v: string | null): string {
  return v ? fmtLocalDate(v) : '';
}

// Project Notes — job-scoped, reuses the EXISTING shared dbo.Notes stack (Custom
// Reporting's job-scoped endpoints; author gate + audit FormName "Project Notes"
// are shared, never reinvented).  Ported verbatim from the QC twin.
type ProjectNote = {
  oid: string; title: string | null; plainText: string | null; text: string | null;
  createdBy: string | null; createdDate: string | null; sort: number; canEdit: boolean;
};
const NOTE_COLS: MiniCol<ProjectNote>[] = [
  { key: 'title',       label: 'Title',  width: 160, text: n => n.title || '' },
  { key: 'note',        label: 'Note',   width: 340, text: n => n.plainText ?? n.text ?? '',
    cell: n => <span style={{ whiteSpace: 'normal' }}>{n.plainText ?? n.text}</span> },
  { key: 'createdBy',   label: 'Author', width: 120, text: n => n.createdBy || '' },
  { key: 'createdDate', label: 'Date',   width: 130, text: n => fmtLocalShort(n.createdDate), sortValue: n => n.createdDate },
];
function NotesModal({ jobOid, jobLabel, onClose }: { jobOid: string; jobLabel: string; onClose: () => void }) {
  const notify = useNotify();
  const base = `/api/v1/reporting/custom/${encodeURIComponent(jobOid)}/project-notes`;
  const [notes, setNotes]   = useState<ProjectNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]     = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editOid, setEditOid]   = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [text, setText]   = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(base); setNotes((r.data as { data?: ProjectNote[] }).data ?? []); }
    catch { notify.error('Could not load project notes.'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobOid]);
  useEffect(() => { void load(); }, [load]);

  const openAdd  = () => { setEditOid(null); setTitle(''); setText(''); setFormOpen(true); };
  const openEdit = (n: ProjectNote) => {
    if (!n.canEdit) { notify.error('You are not authorized to modify a note created by another author.'); return; }
    setEditOid(n.oid); setTitle(n.title ?? ''); setText(n.plainText ?? n.text ?? ''); setFormOpen(true);
  };
  const save = async () => {
    if (!title.trim() || !text.trim()) { notify.warning('Both Title and Note are required.'); return; }
    setBusy(true);
    try {
      if (editOid) {
        await api.put(`/api/v1/reporting/custom/notes/${encodeURIComponent(editOid)}`,
          { title: title.trim(), plainText: text.trim(), sort: 0, form: 'Project Notes' });
      } else {
        await api.post(base, { title: title.trim(), text: text.trim(), noteSource: 'Sample Prep Batch' });
      }
      setFormOpen(false);
      await load();
      prepMsg(editOid ? 'Note updated.' : 'Note added.', 'Project Notes');
    } catch (e) { notify.error(apiErrMsg(e, 'Saving the note failed.')); }
    finally { setBusy(false); }
  };
  const del = async (n: ProjectNote) => {
    if (!n.canEdit) { notify.error('You are not authorized to modify a note created by another author.'); return; }
    setBusy(true);
    try {
      await api.delete(`/api/v1/reporting/custom/project-notes/${encodeURIComponent(n.oid)}`);
      await load();
      prepMsg('Note deleted.', 'Project Notes');
    } catch (e) { notify.error(apiErrMsg(e, 'Deleting the note failed.')); }
    finally { setBusy(false); }
  };

  // Hide / sort / filter / resize (user 2026-09-29, shared useMiniGrid); the popup widens to fit.
  const grid = useMiniGrid<ProjectNote>(NOTE_COLS, notes, {
    rowKey: n => n.oid, loading, empty: 'No project notes for this job.',
    tail: { width: 130, cell: n => n.canEdit && <>
      <button className="btn" onClick={() => openEdit(n)}>Edit</button>{' '}
      <button className="btn" disabled={busy} onClick={() => void del(n)}>Delete</button>
    </> },
  });
  return (
    <Scrim onClose={onClose} width={popupWidthFor(880, grid.width)}>
      <div className="mh notes-mh">🗒 Project Notes — {jobLabel} <span className="x" onClick={onClose}>×</span></div>
      <div className="pb">
        <div style={{ marginBottom: 10 }}>
          <button className="btn primary" onClick={formOpen ? () => setFormOpen(false) : openAdd}>
            {formOpen ? 'Cancel' : '＋ Add Note'}
          </button>
        </div>
        {formOpen && (
          <div style={{ border: '1px solid var(--ai-line)', borderRadius: 6, padding: 12, marginBottom: 12, background: 'var(--ai-header-bg)' }}>
            <input style={{ width: '100%', marginBottom: 8, padding: '6px 9px', border: '1px solid var(--ai-line)', borderRadius: 6 }}
              placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} />
            <textarea style={{ width: '100%', minHeight: 80, padding: '6px 9px', border: '1px solid var(--ai-line)', borderRadius: 6 }}
              placeholder="Note" value={text} onChange={e => setText(e.target.value)} />
            <div style={{ marginTop: 8 }}><button className="btn primary" disabled={busy} onClick={save}>
              {editOid ? 'Update Note' : 'Save Note'}</button></div>
          </div>
        )}
        <div className="gridbox">{grid.table}</div>
      </div>
      <div className="mf"><button className="btn" onClick={onClose}>Close</button></div>
    </Scrim>
  );
}

function Scrim({ onClose, width, children }: { onClose: () => void; width: number; children: ReactNode }) {
  return (
    <div className="qcb-scrim" onClick={onClose}>
      {/* role="dialog" + aria-modal: the pickers are real modal popups, so the shared unsaved-changes
          guard scopes its dirty check to THIS dialog (typing in a picker search box must not arm the
          page-level "Unsaved changes will be lost" prompt) — same fix as the QC Batch form's Scrim. */}
      <div className="pickmodal" role="dialog" aria-modal="true" style={{ width }} onClick={e => e.stopPropagation()}>{children}</div>
    </div>
  );
}

// Copy From Previous Batch — prep twin of QcBatchDetailPage.CopyModal. OG
// SamplePreparationController.btnCopyFromSamplePrepAction_Execute (:6125) +
// CopyingFromPreviousBatch (:6173): pick a prior batch (same test method), tick
// which of its parts to copy — Reagent · Instrument · Others(=Comment) · All the
// Above — OK → append + de-dup into this batch (handled by copyFromPrevious in the
// page). "All the Above" ticks/copies all three.
const COPY_COLS_PrepPreviousBatch: MiniCol<PrepPreviousBatch>[] = [
  { key: 'prepBatchId',         label: 'PrepBatchID',   width: 130, text: b => b.prepBatchId || '' },
  { key: 'matrix',          label: 'Matrix',      width: 110, text: b => b.matrix || '' },
  { key: 'statusText',      label: 'Status',      width: 150, text: b => b.statusText || '' },
  { key: 'createdDate',     label: 'Date',        width: 130, text: b => fmtLocalShort(b.createdDate), sortValue: b => b.createdDate },
  { key: 'reagentCount',    label: 'Reagents',    className: 'num', text: b => String(b.reagentCount), sortValue: b => b.reagentCount },
  { key: 'instrumentCount', label: 'Instruments', className: 'num', text: b => String(b.instrumentCount), sortValue: b => b.instrumentCount },
];
function CopyModal({ testMethodId, excludePrepOid, onClose, onApply }: {
  testMethodId: string; excludePrepOid?: string;
  onClose: () => void; onApply: (sourcePrepOid: string, copyReagents: boolean, copyInstruments: boolean, copyOthers: boolean) => void;
}) {
  const notify = useNotify();
  const [list, setList]       = useState<PrepPreviousBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel]         = useState<string | null>(null);
  // All checkboxes start UNCHECKED on open (matches OG, where the
  // CopyFromSamplePrepBatch BO booleans default false). cpOthers = copy the Comment.
  const [cpReagent, setCpReagent] = useState(false);
  const [cpInstr, setCpInstr]     = useState(false);
  const [cpOthers, setCpOthers]   = useState(false);
  // "All the Above" — master checkbox: checked iff all three are, toggling it
  // sets all three (OG AllAbove → copyReagent = copyInstrument = copyOthers).
  const allAbove = cpReagent && cpInstr && cpOthers;
  const setAllAbove = (v: boolean) => { setCpReagent(v); setCpInstr(v); setCpOthers(v); };
  useEffect(() => {
    prepBatchApi.previousBatches(testMethodId, excludePrepOid)
      .then(setList)
      .catch(() => notify.error('Could not load previous batches.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testMethodId, excludePrepOid]);
  const ok = () => {
    if (!sel) { notify.warning('Select a prep batch to copy from.'); return; }               // legacy guard
    if (!cpReagent && !cpInstr && !cpOthers) { notify.warning('Select at least one item to copy.'); return; }  // legacy "selectchkbox"
    onApply(sel, cpReagent, cpInstr, cpOthers);
  };
  // Hide / sort / filter / resize (user 2026-09-29, shared useMiniGrid); the popup widens to fit.
  const grid = useMiniGrid<PrepPreviousBatch>(COPY_COLS_PrepPreviousBatch, list, {
    rowKey: b => b.prepOid, loading, empty: 'No previous batches for this test.',
    lead: { width: 30, cell: b => <input type="radio" readOnly checked={sel === b.prepOid} /> },
    rowProps: b => ({ className: sel === b.prepOid ? 'sel' : '', style: { cursor: 'pointer' }, onClick: () => setSel(b.prepOid) }),
  });
  return (
    <Scrim onClose={onClose} width={popupWidthFor(760, grid.width)}>
      <div className="mh">📋 Copy From Previous Batch <span className="x" onClick={onClose}>×</span></div>
      <div className="pb">
        <div style={{ display: 'flex', gap: 20, marginBottom: 10, fontSize: 13 }}>
          <label><input type="checkbox" checked={cpReagent} onChange={e => setCpReagent(e.target.checked)} /> Reagent</label>
          <label><input type="checkbox" checked={cpInstr} onChange={e => setCpInstr(e.target.checked)} /> Instrument</label>
          <label><input type="checkbox" checked={cpOthers} onChange={e => setCpOthers(e.target.checked)} /> Others</label>
          <label><input type="checkbox" checked={allAbove} onChange={e => setAllAbove(e.target.checked)} /> All the Above</label>
        </div>
        <div className="gridbox">{grid.table}</div>
      </div>
      <div className="mf"><button className="btn primary" onClick={ok}>OK</button><button className="btn" onClick={onClose}>Cancel</button></div>
    </Scrim>
  );
}

// Add Sample picker — the AI equivalent of OG's SampleLogIn_ListView_Preparation
// popup (SamplePreparationController.AddSample_Execute). Lists the pending-prep
// candidate samples (already excluding the ones in the sequence, done server-side)
// with checkboxes; OK returns the ticked candidates to commitAddSample.
const ADD_SAMPLE_COLS: MiniCol<PrepSample>[] = [
  { key: 'sysSampleCode', label: 'SysSampleCode', width: 160, text: c => c.sysSampleCode || '' },
  { key: 'jobID',         label: 'JobID',         width: 120, text: c => c.jobID || '' },
  { key: 'test',          label: 'Test',          width: 130, text: c => c.test || '' },
  { key: 'method',        label: 'Method',        width: 130, text: c => c.method || '' },
  { key: 'component',     label: 'Component',     width: 150, text: c => c.component || '' },
  { key: 'sampleName',    label: 'SampleName',    width: 180, text: c => c.sampleName || '' },
];
// One candidate per sample + test + component (2026-09-30 / 2026-10-01).
const candKey = (c: PrepSample) => rowKeyOf(c.sampleLogInOid, c.componentOid, c.testMethodOid);
function AddSamplePicker({ candidates, onClose, onCommit }:
  { candidates: PrepSample[]; onClose: () => void; onCommit: (selected: PrepSample[]) => void }) {
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const toggle = (oid: string) =>
    setSel(prev => { const n = new Set(prev); if (n.has(oid)) n.delete(oid); else n.add(oid); return n; });
  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => q
    ? candidates.filter(c => `${c.sysSampleCode ?? ''} ${c.jobID ?? ''} ${c.sampleName ?? ''}`.toLowerCase().includes(q))
    : candidates, [candidates, q]);
  const allChecked = filtered.length > 0 && filtered.every(c => sel.has(candKey(c)));
  const toggleAll = (on: boolean) =>
    setSel(prev => {
      const n = new Set(prev);
      for (const c of filtered) { if (on) n.add(candKey(c)); else n.delete(candKey(c)); }
      return n;
    });
  const ok = () => onCommit(candidates.filter(c => sel.has(candKey(c))));
  // Hide / sort / filter / resize (user 2026-09-29, shared useMiniGrid); the popup widens to fit.
  const grid = useMiniGrid<PrepSample>(ADD_SAMPLE_COLS, filtered, {
    rowKey: candKey, empty: 'No samples available to add.',
    lead: { width: 30,
      head: <input type="checkbox" className="chk" checked={allChecked} onChange={e => toggleAll(e.target.checked)} />,
      cell: c => <input type="checkbox" className="chk" readOnly checked={sel.has(candKey(c))}
        onClick={e => e.stopPropagation()} onChange={() => toggle(candKey(c))} /> },
    rowProps: c => ({ onClick: () => toggle(candKey(c)) }),
  });
  return (
    <Scrim onClose={onClose} width={popupWidthFor(760, grid.width)}>
      <div className="mh">➕ Add Sample <span className="x" onClick={onClose}>×</span></div>
      <div className="pb">
        <div className="list-search" style={{ marginBottom: 8 }}>
          <i className="ti ti-search" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" data-unsaved-ignore />
          {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
        </div>
        <div className="gridbox">{grid.table}</div>
        <div className="foot"><span>{grid.viewRows.length} items</span></div>
      </div>
      <div className="mf">
        <button className="btn primary" disabled={sel.size === 0} onClick={ok}>OK</button>
        <button className="btn" onClick={onClose}>Cancel</button>
      </div>
    </Scrim>
  );
}

// Others-tab instrument picker — TRUE dual-panel design, ported from the QC twin
// (InstrPicker). RIGHT panel opens PRE-SEEDED with the batch's current instruments;
// OK returns the final set; the caller computes the add/remove delta.
function InstrPicker({ preLinked, testMethodId, onClose, onCommit }:
  { preLinked: QcInstrument[]; testMethodId?: string | null;
    onClose: () => void; onCommit: (final: QcInstrument[]) => void }) {
  const notify = useNotify();
  const [options, setOptions] = useState<QcInstrument[]>([]);
  const [loading, setLoading] = useState(true);
  const [staged, setStaged] = useState<QcInstrument[]>(preLinked);
  const [searchL, setSearchL] = useState('');
  const [searchR, setSearchR] = useState('');
  useEffect(() => {
    prepBatchApi.availableInstruments(testMethodId)
      .then(all => {
        const known = new Set(all.map(i => i.instrumentOid));
        const extra = preLinked.filter(i => !known.has(i.instrumentOid));
        setOptions([...all, ...extra]);
      })
      .catch(() => notify.error('Could not load the instrument catalog.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const stagedIds = useMemo(() => new Set(staged.map(s => s.instrumentOid)), [staged]);
  const match = (i: QcInstrument, q: string) =>
    `${i.labwareID ?? ''} ${i.instrumentName ?? ''} ${i.labwareName ?? ''}`.toLowerCase().includes(q);
  const available = useMemo(() => {
    const q = searchL.trim().toLowerCase();
    return options.filter(i => !stagedIds.has(i.instrumentOid) && (!q || match(i, q)));
  }, [options, stagedIds, searchL]);
  const selected = useMemo(() => {
    const q = searchR.trim().toLowerCase();
    return q ? staged.filter(i => match(i, q)) : staged;
  }, [staged, searchR]);
  const addOne = (i: QcInstrument) => setStaged(prev => [...prev, i]);
  const removeOne = (oid: string) => setStaged(prev => prev.filter(s => s.instrumentOid !== oid));
  // Hide / sort / filter / resize on both panels (user 2026-09-29, shared useMiniGrid);
  // the popup widens so a caption never collapses behind its sort/filter icons.
  const cols = useMemo<MiniCol<QcInstrument>[]>(() => [
    { key: 'labwareID',      label: 'InstrumentID',     width: 130, text: i => i.labwareID || '' },
    { key: 'instrumentName', label: 'InstrumentName',   width: 200, text: i => i.instrumentName || '' },
    { key: 'labwareName',    label: 'InstrumentNumber', width: 160, text: i => i.labwareName || '' },
  ], []);
  const left = useMiniGrid(cols, available, {
    rowKey: i => i.instrumentOid, loading, empty: 'No matches',
    lead: { width: 34, cell: i => <button className="circ add" onClick={() => addOne(i)}>＋</button> },
  });
  const right = useMiniGrid(cols, selected, {
    rowKey: i => i.instrumentOid, empty: 'Nothing selected',
    lead: { width: 34, cell: i => <button className="circ rm" onClick={() => removeOne(i.instrumentOid)}>−</button> },
  });
  return (
    <Scrim onClose={onClose} width={popupWidthFor(980, Math.max(left.width, right.width), 2)}>
      <div className="mh">🔬 Instruments <span className="x" onClick={onClose}>×</span></div>
      <div className="pb"><div className="dual">
        <div className="dualcol">
          <div className="ds"><input value={searchL} onChange={e => setSearchL(e.target.value)} placeholder="Enter text to search…" data-unsaved-ignore /></div>
          <div className="dscroll mg">{left.table}</div>
          <div className="dfoot"><span>{left.viewRows.length} items</span><span>Page size: 100</span></div>
        </div>
        <div className="dualcol">
          <div className="ds"><input value={searchR} onChange={e => setSearchR(e.target.value)} placeholder="Enter text to search…" data-unsaved-ignore /></div>
          <div className="dscroll mg">{right.table}</div>
          <div className="dfoot"><span>{right.viewRows.length} items</span><span>Page size: 100</span></div>
        </div>
      </div></div>
      <div className="mf">
        <button className="btn primary" onClick={() => onCommit(staged)}>OK</button>
        <button className="btn" onClick={onClose}>Cancel</button>
      </div>
    </Scrim>
  );
}

// Reagent staged rows are a UNION: pre-linked rows (carry an Oid → remove becomes
// unlinkReagent) OR newly-picked catalog rows (no Oid yet → add becomes
// linkReagent). ReagentID is the merge key. Ported from the QC twin.
type StagedReagent = {
  reagentID:   string;
  reagentName: string | null;
  prepType:    string | null;
  expiryDate:  string | null;
  test:        string | null;
  datePrepared: string | null;
  preparedBy:  string | null;
  dateReceived: string | null;
  linkedOid?:  string;
  catalog?:    QcAvailableReagent;
};
type PreLinkedReagent = {
  oid?: string;
  reagentID:   string | null;
  reagentName: string | null;
  prepType:    string | null;
  expiryDate:  string | null;
  test?:       string | null;
  datePrepared?: string | null;
  preparedBy?: string | null;
  dateReceived?: string | null;
};
// Date-filter above the Available panel (default "1Y"); "All" removes the filter.
const REAGENT_DATE_FILTERS: { id: string; months?: number; years?: number }[] = [
  { id: '1M', months: 1 }, { id: '3M', months: 3 }, { id: '6M', months: 6 },
  { id: '1Y', years: 1 }, { id: '2Y', years: 2 }, { id: '5Y', years: 5 },
  { id: 'All' },
];
function ReagentPicker({ preLinked, testMethodId, onClose, onCommit }:
  { preLinked: PreLinkedReagent[]; testMethodId?: string | null;
    onClose: () => void; onCommit: (final: StagedReagent[]) => void }) {
  const notify = useNotify();
  const [options, setOptions] = useState<QcAvailableReagent[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('1Y');
  const [staged, setStaged] = useState<StagedReagent[]>(
    preLinked
      .filter(p => !!p.reagentID)
      .map(p => ({
        reagentID: p.reagentID!,
        reagentName: p.reagentName,
        prepType: p.prepType,
        expiryDate: p.expiryDate,
        test: p.test ?? null,
        datePrepared: p.datePrepared ?? null,
        preparedBy: p.preparedBy ?? null,
        dateReceived: p.dateReceived ?? null,
        linkedOid: p.oid,
      })));
  const [searchL, setSearchL] = useState('');
  const [searchR, setSearchR] = useState('');
  useEffect(() => {
    // Full OG catalog (user ruling 2026-09-28): the server ignores testMethodId, as OG's
    // SP does — all preparations + vendor lots not disposed (OG 2026-10-08).
    // The Date Filter is applied BY THE SERVER (2026-10-01, "the popup takes a second"):
    // the default 1Y fetches ~3k rows instead of the whole ~14.6k catalog, and changing
    // the filter re-fetches. The client-side filter below stays as a safety net.
    const opt = REAGENT_DATE_FILTERS.find(o => o.id === dateFilter);
    const months = opt?.months ?? (opt?.years ? opt.years * 12 : null);
    let stale = false;
    setLoading(true);
    prepBatchApi.availableReagents(testMethodId, months)
      .then(rows => { if (!stale) setOptions(rows); })
      .catch(() => { if (!stale) notify.error('Could not load the reagent catalog.'); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter]);
  const stagedIds = useMemo(() => new Set(staged.map(s => s.reagentID)), [staged]);
  const match = (id: string, name: string | null, prep: string | null, q: string) =>
    `${id} ${name ?? ''} ${prep ?? ''}`.toLowerCase().includes(q);
  const dateFilterCutoff = useMemo(() => {
    const opt = REAGENT_DATE_FILTERS.find(o => o.id === dateFilter);
    if (!opt || (!opt.months && !opt.years)) return null;
    const d = new Date(); d.setHours(0, 0, 0, 0);   // from TODAY at midnight, as OG (AddMonths(Today(), -n))
    if (opt.months) d.setMonth(d.getMonth() - opt.months);
    if (opt.years) d.setFullYear(d.getFullYear() - opt.years);
    return d;
  }, [dateFilter]);
  const available = useMemo(() => {
    const q = searchL.trim().toLowerCase();
    return options.filter(o => {
      if (!o.reagentID || stagedIds.has(o.reagentID)) return false;
      if (dateFilterCutoff) {
        const raw = o.dateReceived ?? o.datePrepared;
        if (!raw) return false;
        const d = new Date(raw);
        if (Number.isNaN(d.getTime()) || d < dateFilterCutoff) return false;
      }
      return !q || match(o.reagentID, o.reagentName, o.prepType, q);
    });
  }, [options, stagedIds, searchL, dateFilterCutoff]);
  const selected = useMemo(() => {
    const q = searchR.trim().toLowerCase();
    return q ? staged.filter(s => match(s.reagentID, s.reagentName, s.prepType, q)) : staged;
  }, [staged, searchR]);
  const addOne = (o: QcAvailableReagent) => {
    if (!o.reagentID) return;
    setStaged(prev => [...prev, {
      reagentID: o.reagentID!,
      reagentName: o.reagentName,
      prepType: o.prepType,
      expiryDate: o.expiryDate,
      test: o.test,
      datePrepared: o.datePrepared,
      preparedBy: o.preparedBy,
      dateReceived: o.dateReceived,
      catalog: o,
    }]);
  };
  const removeOne = (reagentID: string) => setStaged(prev => prev.filter(s => s.reagentID !== reagentID));
  // Same 8-column set on both panels (legacy parity — see the comment above StagedReagent),
  // widths from the live legacy popup (2026-07-06). Hide / sort / filter / resize via the
  // shared useMiniGrid (user 2026-09-29); a column is never narrower than its caption and
  // each panel scrolls sideways when its grid is wider than the panel.
  type ReagRow = { reagentID: string | null; reagentName: string | null; prepType: string | null; expiryDate: string | null;
    test: string | null; datePrepared: string | null; preparedBy: string | null; dateReceived: string | null };
  const cols = useMemo<MiniCol<ReagRow>[]>(() => [
    { key: 'reagentID',    label: 'ReagentID',      width: 110, text: r => r.reagentID || '' },
    { key: 'reagentName',  label: 'ReagentName',    width: 150, text: r => r.reagentName || '' },
    { key: 'prepType',     label: 'PrepType',       width: 100, text: r => r.prepType || '' },
    { key: 'expiryDate',   label: 'ExpirationDate', width: 110, text: r => fmtDate(r.expiryDate), sortValue: r => r.expiryDate },
    { key: 'test',         label: 'Test',           width: 120, text: r => r.test || '' },
    { key: 'datePrepared', label: 'DatePrepared',   width: 110, text: r => fmtDate(r.datePrepared), sortValue: r => r.datePrepared },
    { key: 'preparedBy',   label: 'PreparedBy',     width: 100, text: r => r.preparedBy || '' },
    { key: 'dateReceived', label: 'DateReceived',   width: 110, text: r => fmtDate(r.dateReceived), sortValue: r => r.dateReceived },
  ], []);
  const left = useMiniGrid<QcAvailableReagent>(cols, available, {
    rowKey: o => o.reagentID ?? '', loading, empty: 'No matches',
    lead: { width: 44, cell: o => <button className="circ add" onClick={() => addOne(o)}>＋</button> },
  });
  const right = useMiniGrid<StagedReagent>(cols, selected, {
    rowKey: s => s.reagentID, empty: 'Nothing selected',
    lead: { width: 44, cell: s => <button className="circ rm" onClick={() => removeOne(s.reagentID)}>−</button> },
  });
  return (
    // Fixed width (user 2026-09-29: "too wide") — two 8-column panels would otherwise
    // widen the popup to the screen edge; each panel's grid scrolls sideways instead.
    <Scrim onClose={onClose} width={1180}>
      <div className="mh">📦 Add Reagents <span className="x" onClick={onClose}>×</span></div>
      <div className="pb">
        <div className="reag-datefilter">
          <label htmlFor="reag-datefilter-sel">Date Filter</label>
          <select id="reag-datefilter-sel" data-unsaved-ignore value={dateFilter} onChange={e => setDateFilter(e.target.value)}>
            {REAGENT_DATE_FILTERS.map(o => <option key={o.id} value={o.id}>{o.id}</option>)}
          </select>
        </div>
        <div className="dual">
        <div className="dualcol">
          <div className="ds"><input value={searchL} onChange={e => setSearchL(e.target.value)} placeholder="Enter text to search…" data-unsaved-ignore /></div>
          <div className="dscroll mg">{left.table}</div>
          <div className="dfoot"><span>{left.viewRows.length} items</span><span>Page size: 20</span></div>
        </div>
        <div className="dualcol">
          <div className="ds"><input value={searchR} onChange={e => setSearchR(e.target.value)} placeholder="Enter text to search…" data-unsaved-ignore /></div>
          <div className="dscroll mg">{right.table}</div>
          <div className="dfoot"><span>{right.viewRows.length} items</span><span>Page size: 20</span></div>
        </div>
      </div></div>
      <div className="mf">
        <button className="btn primary" onClick={() => onCommit(staged)}>OK</button>
        <button className="btn" onClick={onClose}>Cancel</button>
      </div>
    </Scrim>
  );
}
